/*
  Edward Fields · Interacciones
  --------------------------------------------------------------------------
  Un solo archivo para todas las animaciones e interacciones de la home y la PDP.

  Cómo funciona
  - Nada se engancha por clase de CSS: todo va por atributos `data-ef-*`.
    Así puedes maquetar las secciones como quieras sin romper el JS.
  - Se inicializa solo al cargar la página y se vuelve a inicializar en cada
    sección que el editor de temas recarga (`shopify:section:load`).
  - Es idempotente: una sección ya inicializada no se vuelve a enganchar.
  - Respeta `prefers-reduced-motion`: sin autoplay, sin bucles, sin parallax.

  Uso en el tema
    {{ 'ef-interactions.js' | asset_url | script_tag }}
  o mejor, con defer:
    <script src="{{ 'ef-interactions.js' | asset_url }}" defer></script>
*/
(function () {
  'use strict';

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var INIT = 'efInit';

  /* Observador reutilizable: ejecuta fn(el, visible) al entrar o salir de pantalla */
  function onView(el, fn, threshold, once) {
    if (!('IntersectionObserver' in window)) { fn(el, true); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        fn(e.target, e.isIntersecting, e);
        if (once && e.isIntersecting) io.unobserve(e.target);
      });
    }, { threshold: threshold == null ? 0.2 : threshold });
    io.observe(el);
    return io;
  }

  var M = {}; // registro de módulos

  /* ======================================================================
     1 · REVEAL — aparición al hacer scroll
     <div data-ef-reveal>            · sube y aparece
     <div data-ef-reveal="80">       · con 80 ms de retardo
     <div data-ef-reveal-group="150"> … <x data-ef-reveal> …   · sin valor: retardo = orden dentro del grupo × 150
     CSS: [data-ef-reveal] { opacity:0; transform:translateY(22px) }
          [data-ef-reveal].is-in { opacity:1; transform:none }
     ====================================================================== */
  M.reveal = function (root) {
    $$('[data-ef-reveal]', root).forEach(function (el) {
      if (reduce) { el.classList.add('is-in'); return; }
      var delay = parseInt(el.getAttribute('data-ef-reveal'), 10) || 0;
      var group = el.getAttribute('data-ef-reveal') === '' && el.closest('[data-ef-reveal-group]');
      if (group) {
        var step = parseInt(group.getAttribute('data-ef-reveal-group'), 10) || 120;
        delay = $$('[data-ef-reveal]', group).indexOf(el) * step;
      }
      onView(el, function (t, vis) {
        if (vis) setTimeout(function () { t.classList.add('is-in'); }, delay);
      }, 0.15, true);
    });
  };

  /* ======================================================================
     2 · MARQUESINA — cinta de claims en bucle
     <div data-ef-marquee data-ef-speed="60">   · 60 s por vuelta
       <div data-ef-marquee-track>  …dos copias idénticas del contenido…
     Se pausa al pasar el ratón (CSS) y se duplica el contenido si hace falta.
     ====================================================================== */
  M.marquee = function (root) {
    $$('[data-ef-marquee]', root).forEach(function (el) {
      var track = $('[data-ef-marquee-track]', el);
      if (!track) return;
      var speed = parseFloat(el.getAttribute('data-ef-speed')) || 45;
      track.style.animationDuration = speed + 's';
      if (reduce) track.style.animation = 'none';
      // duplica el contenido para que el bucle no tenga huecos
      if (track.children.length === 1) track.appendChild(track.firstElementChild.cloneNode(true));
    });
  };

  /* ======================================================================
     3 · PESTAÑAS — "Elige tu pack del mes" (píldora deslizante)
     <div data-ef-tabs>
       <span data-ef-tabs-pill></span>
       <button data-ef-tab="mes" data-ef-note="Te llega cada mes…">…</button>
       <p data-ef-tabs-note></p>
       <div data-ef-tabpanel="mes">…</div>   (opcional)
     ====================================================================== */
  M.tabs = function (root) {
    $$('[data-ef-tabs]', root).forEach(function (box) {
      var tabs = $$('[data-ef-tab]', box), pill = $('[data-ef-tabs-pill]', box), note = $('[data-ef-tabs-note]', box);
      if (!tabs.length) return;
      function move(t) {
        if (!pill) return;
        pill.style.width = t.offsetWidth + 'px';
        pill.style.transform = 'translateX(' + t.offsetLeft + 'px)';
      }
      function select(t) {
        tabs.forEach(function (x) { x.setAttribute('aria-selected', String(x === t)); });
        move(t);
        if (note && t.getAttribute('data-ef-note')) note.textContent = t.getAttribute('data-ef-note');
        var key = t.getAttribute('data-ef-tab');
        $$('[data-ef-tabpanel]', box.closest('[data-ef-section]') || document).forEach(function (p) {
          p.hidden = p.getAttribute('data-ef-tabpanel') !== key;
        });
        box.dispatchEvent(new CustomEvent('ef:tab', { detail: { key: key }, bubbles: true }));
      }
      tabs.forEach(function (t) { t.addEventListener('click', function () { select(t); }); });
      var active = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
      select(active);
      window.addEventListener('resize', function () { move(active); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { move(active); });
    });
  };

  /* ======================================================================
     4 · VÍDEO EN PANTALLA — reproduce solo mientras se ve
     <video data-ef-autoplay muted loop playsinline preload="none" poster="…">
     Ahorra batería y datos: fuera de pantalla se pausa.
     ====================================================================== */
  M.autoplay = function (root) {
    $$('[data-ef-autoplay]', root).forEach(function (v) {
      v.muted = true;
      if (reduce) return;
      onView(v, function (el, vis) {
        if (vis) { if (el.preload === 'none') el.preload = 'auto'; var p = el.play(); if (p) p.catch(function () {}); }
        else el.pause();
      }, 0.25);
    });
  };

  /* ======================================================================
     5 · GALERÍA DE PRODUCTO (PDP)
     <div data-ef-gallery>
       <div data-ef-slide>…img o video…</div>  (xN)
       <button data-ef-gallery-prev> <button data-ef-gallery-next>
       <div data-ef-thumbs></div>   · las miniaturas se generan solas
     Teclado, deslizar con el dedo y miniatura activa sincronizada.
     ====================================================================== */
  M.gallery = function (root) {
    $$('[data-ef-gallery]', root).forEach(function (g) {
      var slides = $$('[data-ef-slide]', g), thumbs = $('[data-ef-thumbs]', g), cur = 0;
      if (!slides.length) return;

      if (thumbs && !thumbs.children.length) {
        slides.forEach(function (s, i) {
          var media = s.querySelector('img,video');
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ef-thumb';
          b.setAttribute('data-ef-thumb', String(i));
          b.setAttribute('aria-label', 'Ver imagen ' + (i + 1));
          var src = media && (media.tagName === 'VIDEO' ? media.getAttribute('poster') : media.currentSrc || media.src);
          b.innerHTML = '<img src="' + (src || '') + '" alt="" loading="lazy">' +
            (media && media.tagName === 'VIDEO' ? '<span class="ef-thumb__play" aria-hidden="true"></span>' : '');
          b.addEventListener('click', function () { go(i); });
          thumbs.appendChild(b);
        });
      }

      function go(i) {
        cur = (i + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          s.classList.toggle('is-on', k === cur);
          var v = s.querySelector('video');
          if (v) { if (k === cur && !reduce) { v.play().catch(function () {}); } else { v.pause(); } }
        });
        $$('[data-ef-thumb]', g).forEach(function (t, k) {
          t.setAttribute('aria-current', String(k === cur));
          // solo se desplaza el carril de miniaturas (scrollIntoView movía también la página, p. ej. al cambiar
          // de variante en móvil con la galería fuera de pantalla)
          if (k === cur && thumbs && thumbs.scrollWidth > thumbs.clientWidth + 1) {
            var cr = thumbs.getBoundingClientRect(), tr = t.getBoundingClientRect();
            var left = thumbs.scrollLeft + (tr.left - cr.left) - (cr.width - tr.width) / 2;
            if (thumbs.scrollTo) thumbs.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' }); else thumbs.scrollLeft = left;
          }
        });
        g.dispatchEvent(new CustomEvent('ef:slide', { detail: { index: cur }, bubbles: true }));
      }
      /* Imagen de la variante (la llama el módulo buybox con los datos de cada variante):
         - si su foto (media) está entre los slides ([data-ef-media-id]) → se va a ese slide;
         - si no (p. ej. cápsulas: la galería solo enseña la 1.ª foto + infografías) o viene de assets/ → se cambia
           la imagen del slide [data-ef-slide-variant] (o el primero) y su miniatura, y se muestra ese slide;
         - variante sin imagen propia → ese slide vuelve a su portada original. */
      function variantSlide() { return $('[data-ef-slide-variant]', g) || slides[0]; }
      function orig(slide) { // portada original del slide (si el Liquid ya la sustituyó, viene en data-ef-orig-*)
        var img = slide && slide.querySelector('img');
        if (img && !slide._efOrig) {
          var o = img.hasAttribute('data-ef-orig-src');
          slide._efOrig = { src: o ? img.getAttribute('data-ef-orig-src') : img.getAttribute('src'),
            srcset: o ? img.getAttribute('data-ef-orig-srcset') : img.getAttribute('srcset') };
        }
        return slide && slide._efOrig;
      }
      function setImg(slide, src, srcset) {
        var img = slide && slide.querySelector('img');
        if (!img || !src) return false;
        orig(slide);
        if (img.getAttribute('src') !== src) {
          if (srcset) img.setAttribute('srcset', srcset); else img.removeAttribute('srcset');
          img.setAttribute('src', src);
        }
        var t = $('[data-ef-thumb="' + slides.indexOf(slide) + '"] img', g);
        if (t) t.setAttribute('src', src);
        return true;
      }
      function showVariant(v) {
        if (!v) return;
        var vs = variantSlide(), target = -1;
        if (v.media != null && !v.asset) {
          slides.forEach(function (s, k) { if (target < 0 && s.getAttribute('data-ef-media-id') === String(v.media)) target = k; });
        }
        if (target < 0 && v.img) {
          if (setImg(vs, v.img, v.srcset)) target = slides.indexOf(vs);
        } else {
          var o = orig(vs), img = vs && vs.querySelector('img');
          if (o && img && img.getAttribute('src') !== o.src && slides.indexOf(vs) !== target) {
            setImg(vs, o.src, o.srcset); // el slide sustituido vuelve a su portada
            if (target < 0) target = slides.indexOf(vs); // variante sin imagen propia: se enseña la portada
          }
        }
        if (target >= 0 && target !== cur) go(target);
      }
      g._efGallery = { go: go, showVariant: showVariant };
      var prev = $('[data-ef-gallery-prev]', g), next = $('[data-ef-gallery-next]', g);
      if (prev) prev.addEventListener('click', function () { go(cur - 1); });
      if (next) next.addEventListener('click', function () { go(cur + 1); });
      g.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') go(cur + 1);
        if (e.key === 'ArrowLeft') go(cur - 1);
      });
      var x0 = 0;
      g.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      g.addEventListener('touchend', function (e) {
        var d = e.changedTouches[0].clientX - x0;
        if (Math.abs(d) > 45) go(cur + (d < 0 ? 1 : -1));
      }, { passive: true });
      // slide inicial: [data-ef-slide-start] (la foto de la variante seleccionada, lo marca el Liquid) o el primero
      var start = slides.indexOf($('[data-ef-slide-start]', g));
      go(start > 0 ? start : 0);
    });
  };

  /* ======================================================================
     6 · VÍDEO POR CAPÍTULOS — "Tan fácil como tu café"
     <div data-ef-chapters>
       <video data-ef-chapters-video src="…" muted loop playsinline></video>
       <button data-ef-chapter data-from="0" data-to="3.04"><i data-ef-progress></i></button>
     Al pasar el ratón (o enfocar) un capítulo, el vídeo salta a ese tramo y lo repite mientras sigas encima;
     al salir, sigue la secuencia normal desde donde está. Tocar o hacer clic salta al tramo y continúa normal.
     ====================================================================== */
  M.chapters = function (root) {
    $$('[data-ef-chapters]', root).forEach(function (box) {
      var v = $('[data-ef-chapters-video]', box), chaps = $$('[data-ef-chapter]', box), held = null, touch = false;
      if (!v || !chaps.length) return;
      function mark(i) {
        chaps.forEach(function (c, k) { c.setAttribute('aria-current', String(k === i)); });
        // encuadre del capítulo (data-pos-x, %): lo usa el CSS en móvil, donde el vídeo se recorta en vertical
        var x = chaps[i].getAttribute('data-pos-x');
        if (x !== null && x !== '') box.style.setProperty('--ef-chapter-x', x + '%');
      }
      function seek(i) {
        var from = parseFloat(chaps[i].getAttribute('data-from')) + 0.05;
        mark(i);
        var doSeek = function () { try { v.currentTime = from; } catch (e) {} var p = v.play(); if (p) p.catch(function () {}); };
        if (v.readyState >= 1) doSeek();
        else { v.addEventListener('loadedmetadata', doSeek, { once: true }); v.load(); }
      }
      function hold(i) { held = i; chaps.forEach(function (x, k) { x.classList.toggle('is-held', k === i); }); if (i !== null) seek(i); }
      chaps.forEach(function (c, i) {
        c.addEventListener('pointerdown', function (e) { touch = e.pointerType !== 'mouse'; });
        c.addEventListener('mouseenter', function () { if (!touch) hold(i); });
        c.addEventListener('mouseleave', function () { if (!touch && held === i) hold(null); });
        c.addEventListener('focus', function () { if (!touch) hold(i); });
        c.addEventListener('blur', function () { if (held === i) hold(null); });
        c.addEventListener('click', function () { if (touch) { hold(null); seek(i); } });
      });
      v.addEventListener('timeupdate', function () {
        var t = v.currentTime;
        if (held !== null) {
          var a = parseFloat(chaps[held].getAttribute('data-from')), b = parseFloat(chaps[held].getAttribute('data-to'));
          if (t >= b - 0.05 || t < a - 0.05) { v.currentTime = a + 0.05; return; }
        }
        chaps.forEach(function (c, i) {
          var a = parseFloat(c.getAttribute('data-from')), b = parseFloat(c.getAttribute('data-to'));
          var on = t >= a && t < b;
          if (on && c.getAttribute('aria-current') !== 'true') mark(i);
          var bar = $('[data-ef-progress]', c);
          if (bar) bar.style.width = on ? Math.min(100, (t - a) / (b - a) * 100) + '%' : (t >= b ? '100%' : '0');
        });
      });
    });
  };

  /* ======================================================================
     7 · PASOS DESTACADOS — "Cómo funciona la suscripción"
     <div data-ef-steps data-ef-interval="4200">
       <button data-ef-step>…</button>  (xN)
     Rotan solos; al pasar el ratón o tocar, se fija ese paso.
     ====================================================================== */
  M.steps = function (root) {
    $$('[data-ef-steps]', root).forEach(function (box) {
      var steps = $$('[data-ef-step]', box), i = 0, timer = null;
      if (!steps.length) return;
      var every = parseInt(box.getAttribute('data-ef-interval'), 10) || 4200;
      function set(n) { i = n; steps.forEach(function (s, k) { s.setAttribute('aria-current', String(k === n)); }); }
      function auto() { clearInterval(timer); if (reduce) return; timer = setInterval(function () { set((i + 1) % steps.length); }, every); }
      // Si los pasos son un carrusel deslizable (móvil: data-ef-steps en el mismo track que data-ef-slider),
      // el paso activo es el que está a la vista y no rota solo (si no, se encendía uno que no se ve).
      function scrollable() { return box.scrollWidth > box.clientWidth + 5 && getComputedStyle(box).overflowX !== 'visible'; }
      function nearest() {
        var best = 0, d = 1e9, left = box.getBoundingClientRect().left + (parseFloat(getComputedStyle(box).paddingLeft) || 0);
        steps.forEach(function (s, k) { var dd = Math.abs(s.getBoundingClientRect().left - left); if (dd < d) { d = dd; best = k; } });
        return best;
      }
      function autoOrScroll() { if (scrollable()) { clearInterval(timer); set(nearest()); } else auto(); }
      box.addEventListener('scroll', function () { if (scrollable()) requestAnimationFrame(function () { set(nearest()); }); }, { passive: true });
      steps.forEach(function (s, k) {
        s.addEventListener('click', function () { set(k); if (!scrollable()) auto(); });
        s.addEventListener('mouseenter', function () { if (!scrollable()) { set(k); clearInterval(timer); } });
        s.addEventListener('mouseleave', function () { if (!scrollable()) auto(); });
      });
      set(0);
      onView(box, function (el, vis) { if (vis) autoOrScroll(); else clearInterval(timer); }, 0.3);
      window.addEventListener('resize', function () { if (scrollable()) { clearInterval(timer); set(nearest()); } });
    });
  };

  /* ======================================================================
     8 · CONTADOR — números que suben al aparecer
     <b data-ef-count="30" data-ef-suffix=" s">30 s</b>
     Opcionales (manifiesto): data-ef-prefix="+" · data-ef-count-from="2014" (cuenta desde ahí, también hacia abajo:
     from 15 → 0) · data-ef-count-delay="900" (ms). Si está dentro de [data-ef-inview], arranca cuando entra ese
     contenedor (con su umbral) en vez de cuando entra el propio número.
     Opcionales (profesionales, referencia/profesionales.html data-pad / miles): data-ef-count-pad="2" (01, 02…) ·
     data-ef-count-group="." (separador de miles: 2.200). Sin ellos el número se pinta igual que siempre.
     ====================================================================== */
  M.count = function (root) {
    $$('[data-ef-count]', root).forEach(function (el) {
      var to = parseFloat(el.getAttribute('data-ef-count')), suf = el.getAttribute('data-ef-suffix') || '';
      var pre = el.getAttribute('data-ef-prefix') || '', from = parseFloat(el.getAttribute('data-ef-count-from')) || 0;
      var delay = parseInt(el.getAttribute('data-ef-count-delay'), 10) || 0;
      var pad = parseInt(el.getAttribute('data-ef-count-pad'), 10) || 0, grp = el.getAttribute('data-ef-count-group') || '';
      function fmt(n, raw) {
        if (!pad && !grp) return pre + (raw ? n : Math.round(n)) + suf;
        var s = String(Math.round(n));
        if (grp) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, grp);
        while (s.length < pad) s = '0' + s;
        return pre + s + suf;
      }
      if (reduce || to === from) { el.textContent = fmt(to, true); return; }
      var box = el.closest('[data-ef-inview]');
      var th = box ? parseFloat(box.getAttribute('data-ef-inview')) || 0.3 : 0.6;
      if (from) el.textContent = fmt(from, true);
      onView(box || el, function (t, vis) {
        if (!vis) return;
        setTimeout(function () {
          var t0 = performance.now(), dur = 1000;
          (function step(now) {
            var k = Math.min(1, (now - t0) / dur);
            to = parseFloat(el.getAttribute('data-ef-count')); // puede cambiar mientras cuenta (vcount · módulo 41)
            el.textContent = fmt(from + (to - from) * (1 - Math.pow(1 - k, 3)));
            if (k < 1) requestAnimationFrame(step);
          })(t0);
        }, delay);
      }, th, true);
    });
  };

  /* ======================================================================
     9 · TARJETA 3D — se inclina siguiendo el ratón (no con el dedo ni con reduce)
     <div data-ef-tilt data-ef-tilt-max="14">
     ====================================================================== */
  M.tilt = function (root) {
    if (reduce) return;
    $$('[data-ef-tilt]', root).forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-ef-tilt-max')) || 14;
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return; // táctil: sin inclinación (referencia/manifiesto.html [data-tilt])
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = 'perspective(700px) rotateY(' + (x * max) + 'deg) rotateX(' + (-y * max) + 'deg) translateY(-4px)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  };

  /* ======================================================================
     10 · CARRUSEL HORIZONTAL — reseñas y productos
     <div data-ef-slider>
       <div data-ef-track><article data-ef-item>…</article></div>
       <button data-ef-slider-prev> <button data-ef-slider-next>
       <div data-ef-dots></div>
     Arrastre con ratón, puntos y flechas que se desactivan en los extremos.
     ====================================================================== */
  M.slider = function (root) {
    $$('[data-ef-slider]', root).forEach(function (box) {
      var track = $('[data-ef-track]', box), items = $$('[data-ef-item]', box), dots = $('[data-ef-dots]', box);
      if (!track || !items.length) return;
      var pad = function () { return parseFloat(getComputedStyle(track).paddingLeft) || 0; };
      var maxS = function () { return track.scrollWidth - track.clientWidth; };
      /* Última parada real: la primera tarjeta que ya entra entera cuando el track está al final. Las de después no
         son paradas (no se puede scrollear más), así que no llevan punto propio. */
      function last() {
        var m = maxS();
        for (var i = 0; i < items.length; i++) if (items[i].offsetLeft - pad() >= m - 5) return i;
        return items.length - 1;
      }
      function idx() {
        var L = last();
        if (track.scrollLeft >= maxS() - 5) return L;
        var best = 0, d = 1e9;
        for (var i = 0; i <= L; i++) { var dd = Math.abs(items[i].offsetLeft - pad() - track.scrollLeft); if (dd < d) { d = dd; best = i; } }
        return best;
      }
      function go(i) {
        i = Math.max(0, Math.min(last(), i));
        track.scrollTo({ left: Math.min(items[i].offsetLeft - pad(), maxS()), behavior: 'smooth' });
      }
      /* Un punto por parada (se rehacen al cambiar el ancho). Si la sección trae sus propios puntos, se respetan. */
      var ownDots = dots && dots.children.length;
      function buildDots() {
        if (!dots || ownDots) return;
        var n = last() + 1;
        if (dots.children.length === n) return;
        dots.innerHTML = '';
        for (var i = 0; i < n; i++) (function (i) {
          var b = document.createElement('button');
          b.type = 'button'; b.setAttribute('aria-label', 'Ir a la posición ' + (i + 1) + ' de ' + n);
          b.addEventListener('click', function () { go(i); });
          dots.appendChild(b);
        })(i);
      }
      buildDots();
      var prev = $('[data-ef-slider-prev]', box), next = $('[data-ef-slider-next]', box);
      function sync() {
        var i = idx();
        if (dots) $$('button', dots).forEach(function (b, k) { b.classList.toggle('is-on', k === i); });
        if (prev) prev.disabled = track.scrollLeft < 5;
        if (next) next.disabled = i >= last() || track.scrollLeft > maxS() - 5;
      }
      if (prev) prev.addEventListener('click', function () { go(idx() - 1); });
      if (next) next.addEventListener('click', function () { go(idx() + 1); });
      track.addEventListener('scroll', function () { requestAnimationFrame(sync); }, { passive: true });
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') go(idx() + 1);
        if (e.key === 'ArrowLeft') go(idx() - 1);
      });
      var down = false, x0 = 0, s0 = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        down = true; x0 = e.clientX; s0 = track.scrollLeft; track.classList.add('is-grabbing');
      });
      window.addEventListener('pointermove', function (e) { if (down) track.scrollLeft = s0 - (e.clientX - x0); });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false; track.classList.remove('is-grabbing'); go(idx());
      });
      sync();
      window.addEventListener('resize', function () { buildDots(); sync(); });
      if (document.fonts) document.fonts.ready.then(function () { buildDots(); sync(); });
    });
  };

  /* ======================================================================
     11 · ACORDEÓN — FAQ y fichas (uno abierto a la vez)
     <div data-ef-accordion><details>…</details></div>
     ====================================================================== */
  M.accordion = function (root) {
    $$('[data-ef-accordion]', root).forEach(function (box) {
      var all = $$('details', box);
      all.forEach(function (d) {
        var s = d.querySelector('summary');
        if (!s) return;
        s.addEventListener('click', function () {
          if (!d.open) all.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });
  };

  /* ======================================================================
     12 · CÁPSULA DEL RITUAL — dos vídeos que se cruzan
     <div data-ef-capsule>
       <video data-ef-capsule-idle>  · gira en bucle
       <video data-ef-capsule-open>  · se abre y muestra el matcha
     <button data-ef-capsule-trigger data-ef-dot="d1">  · tarjetas de beneficio
     ====================================================================== */
  M.capsule = function (root) {
    $$('[data-ef-capsule]', root).forEach(function (cap) {
      var open = $('[data-ef-capsule-open]', cap), timer = null;
      var scope = cap.closest('[data-ef-section]') || root || document;
      function show(on) {
        clearTimeout(timer);
        if (on) {
          if (!cap.classList.contains('is-open') && open) { open.currentTime = 0; var p = open.play(); if (p) p.catch(function () {}); }
          cap.classList.add('is-open');
        } else {
          timer = setTimeout(function () { cap.classList.remove('is-open'); if (open) setTimeout(function () { open.pause(); }, 400); }, 900);
        }
      }
      /* Ratón: hover. Teclado: foco. Táctil: un toque abre, otro toque en la misma tarjeta cierra, tocar otra cambia.
         (En táctil el navegador emula mouseenter + focus antes del click: se ignoran para que el click no lo deshaga.) */
      var active = null, touch = false;
      $$('[data-ef-capsule-trigger]', scope).forEach(function (card) {
        var dotId = card.getAttribute('data-ef-dot');
        var dot = dotId ? $('#' + dotId, scope) : null;
        function hot(on) {
          if (dot) dot.classList.toggle('is-on', on);
          card.classList.toggle('is-on', on);
          cap.closest('[data-ef-section]') && cap.closest('[data-ef-section]').classList.toggle('is-hot', on);
          show(on);
        }
        card._efHot = hot;
        card.addEventListener('pointerdown', function (e) { touch = e.pointerType !== 'mouse'; });
        card.addEventListener('mouseenter', function () { if (!touch) hot(true); });
        card.addEventListener('mouseleave', function () { if (!touch) hot(false); });
        card.addEventListener('focus', function () { if (!touch) hot(true); });
        card.addEventListener('blur', function () { if (!touch) hot(false); });
        card.addEventListener('click', function () {
          if (!touch) return;
          if (active === card) { hot(false); active = null; return; }
          if (active && active._efHot) active._efHot(false);
          hot(true); active = card;
        });
      });
      document.addEventListener('pointerdown', function (e) {
        if (active && e.pointerType !== 'mouse' && !e.target.closest('[data-ef-capsule-trigger]')) { active._efHot(false); active = null; }
      });
    });
  };

  /* ======================================================================
     13 · BARRA DE COMPRA FIJA (PDP)
     <div data-ef-stickybar data-ef-after="#AddToCart">
     Aparece cuando el botón de compra original sale de pantalla.
     En móvil aparece a partir de 120 px de scroll.
     ====================================================================== */
  M.stickybar = function (root) {
    $$('[data-ef-stickybar]', root).forEach(function (bar) {
      var sel = bar.getAttribute('data-ef-after');
      var anchor = sel ? $(sel) : null;
      if (anchor) {
        onView(anchor, function (el, vis, e) {
          bar.classList.toggle('is-on', !vis && e.boundingClientRect.top < 0);
        }, 0);
      }
      if (window.matchMedia('(max-width:860px)').matches) {
        window.addEventListener('scroll', function () {
          if (window.scrollY > 120) bar.classList.add('is-on');
        }, { passive: true });
      }
    });
  };

  /* ======================================================================
     14 · FILA RESALTADA — comparativa "No todo el matcha es igual"
     <div data-ef-compare>  …  <li data-ef-row="2">
     Al pasar por una fila se resalta la misma en las dos tarjetas.
     ====================================================================== */
  M.compare = function (root) {
    $$('[data-ef-compare]', root).forEach(function (box) {
      box.addEventListener('mouseover', function (e) {
        var li = e.target.closest('[data-ef-row]');
        $$('.is-hot', box).forEach(function (x) { x.classList.remove('is-hot'); });
        if (li) $$('[data-ef-row="' + li.getAttribute('data-ef-row') + '"]', box).forEach(function (x) { x.classList.add('is-hot'); });
      });
      box.addEventListener('mouseleave', function () {
        $$('.is-hot', box).forEach(function (x) { x.classList.remove('is-hot'); });
      });
    });
  };

  /* ======================================================================
     15 · REEL DE CLIENTE — silencio por defecto, sonido al tocar
     <button data-ef-reel><video muted loop playsinline …></video></button>
     Al activar el sonido vuelve al segundo 0 y silencia los otros reels de la sección.
     ====================================================================== */
  M.reel = function (root) {
    $$('[data-ef-reel]', root).forEach(function (r) {
      var v = r.querySelector('video');
      if (!v) return;
      v.muted = true;
      if (!reduce) onView(r, function (el, vis) {
        if (vis) { v.preload = 'auto'; var p = v.play(); if (p) p.catch(function () {}); } else v.pause();
      }, 0.3);
      r.addEventListener('click', function () {
        v.muted = !v.muted;
        if (!v.muted) {
          v.currentTime = 0;
          // solo un reel con sonido a la vez: los otros de la misma sección vuelven a silencio
          var scope = r.closest('[data-ef-section]') || document;
          $$('[data-ef-reel]', scope).forEach(function (o) {
            if (o === r) return;
            var ov = o.querySelector('video');
            if (ov) ov.muted = true;
            o.classList.remove('is-playing');
          });
        }
        v.play().catch(function () {});
        r.classList.toggle('is-playing', !v.muted);
      });
    });
  };

  /* ======================================================================
     16 · NAV SÓLIDO — el menú pasa a fondo sólido al bajar del hero
     <div data-ef-navsolid='[data-ef-section="ef-hero"]' data-ef-navsolid-offset="90">
     Clase .is-solid cuando scrollY > alto del hero − offset (90 por defecto).
     <div data-ef-navsolid data-ef-navsolid-at="8">  · sólido apenas se scrollea más de 8 px
     El hero se busca en cada scroll: sigue funcionando si el editor recarga esa sección.
     ====================================================================== */
  M.navsolid = function (root) {
    $$('[data-ef-navsolid]', root).forEach(function (nav) {
      var sel = nav.getAttribute('data-ef-navsolid') || '[data-ef-section="ef-hero"]';
      var off = parseFloat(nav.getAttribute('data-ef-navsolid-offset'));
      var at = parseFloat(nav.getAttribute('data-ef-navsolid-at'));
      if (isNaN(off)) off = 90;
      function set() {
        if (!isNaN(at)) { nav.classList.toggle('is-solid', window.scrollY > at); return; }
        var hero = $(sel);
        nav.classList.toggle('is-solid', !hero || window.scrollY > hero.offsetHeight - off);
      }
      window.addEventListener('scroll', set, { passive: true });
      window.addEventListener('resize', set);
      set();
    });
  };

  /* ======================================================================
     17 · MENÚ MOBILE — panel a pantalla completa (sections/ef-mobile-menu.liquid)
     <div id="ef-mmenu" data-ef-mmenu> … <button data-ef-mmenu-close> …
     Lo abre la hamburguesa del header: el <summary> del drawer de Dawn (header-drawer) o cualquier
     [data-ef-mmenu-toggle]. Si la sección no está, el drawer de Dawn funciona como siempre.
     Cierra con ✕, Escape, al tocar un enlace del panel o al pasar a desktop (≥ 990). Bloquea el scroll de la página
     y arranca justo debajo del top bar si está a la vista (--ef-mmenu-top).
     ====================================================================== */
  M.mmenu = function (root) {
    $$('[data-ef-mmenu]', root).forEach(function (panel) {
      if (panel._efMmenu) return;
      panel._efMmenu = true;
      panel.setAttribute('inert', '');
      panel.addEventListener('click', function (e) {
        if (e.target.closest('[data-ef-mmenu-close]') || e.target.closest('a[href]')) mmenuClose();
      });
    });
    if (window._efMmenuBound) return;
    window._efMmenuBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target.closest('header-drawer summary, [data-ef-mmenu-toggle]');
      var panel = document.getElementById('ef-mmenu');
      if (!t || !panel || window.innerWidth >= 990) return;
      e.preventDefault();
      e.stopImmediatePropagation(); // el drawer de Dawn no se abre
      if (panel.classList.contains('is-open')) mmenuClose(); else mmenuOpen(panel, t);
    }, true);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') mmenuClose(); });
    // iOS Safari solo aplica :active si hay un touchstart escuchando: la respuesta al tocar (ef-animations.css · 26b)
    document.addEventListener('touchstart', function () {}, { passive: true });
    window.addEventListener('resize', function () { if (window.innerWidth >= 990) mmenuClose(); });
  };
  var mmenuFrom = null;
  function mmenuOpen(panel, from) {
    var bar = document.querySelector('.section-ef-top-bar');
    var top = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
    panel.style.setProperty('--ef-mmenu-top', top + 'px');
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    document.documentElement.classList.add('ef-mmenu-lock');
    mmenuFrom = from;
    if (from) from.setAttribute('aria-expanded', 'true');
    var x = panel.querySelector('[data-ef-mmenu-close]');
    if (x) x.focus({ preventScroll: true });
  }
  function mmenuClose() {
    var panel = document.getElementById('ef-mmenu');
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
    document.documentElement.classList.remove('ef-mmenu-lock');
    if (mmenuFrom) { mmenuFrom.setAttribute('aria-expanded', 'false'); mmenuFrom.focus({ preventScroll: true }); }
    mmenuFrom = null;
  }

  /* ======================================================================
     18 · CAJA DE COMPRA (PDP) — sections/ef-main-product.liquid
     <div data-ef-buybox> con <script type="application/json" data-ef-buybox-data> (variantes y precios por plan)
     Radios [data-ef-buybox-opt="i"] (opción i de la variante) y [data-ef-buybox-planopt] (selling plan; "" = compra
     única). Actualiza: input name=id y name=selling_plan, precios de cada opción y de cada plan, precio principal
     (+ "/mes" con plan), precio por unidad (número de la variante, p. ej. "30 Cápsulas"), texto del botón (también el
     de la barra fija) y ?variant= en la URL (y ?selling_plan=, si ya venía). Con un plan de Recharge (selling plan
     nativo) el carrito lo recibe. El plan marcado al cargar lo decide el Liquid (por defecto, Compra única).
     Imagen por variante: cada variante del JSON lleva "media" (id de su foto en Shopify), "img"/"srcset" (1040/520)
     y "thumb" (104), o "asset": true si la foto sale del ajuste "Imagen por variante desde assets". Se la pasa a la
     galería (gallery · showVariant) y a la miniatura de la barra fija ([data-ef-buybox-barimg]).
     Tachado: [data-ef-buybox-was] con el compare_at_price de la variante ("compare" en el JSON) si "showWas" (producto
     sin planes) y [data-ef-buybox-off] con "offText" ("−[N] %", solo en productos de liquidación).
     Al terminar emite ef:variant (detail: variant, plan) en la caja: lo escucha el módulo 41 (vcount).
     ====================================================================== */
  function efMoney(cents, fmt) {
    var n = (cents / 100);
    function sep(x, dec, th, dc) {
      var p = x.toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return p.join(dc);
    }
    return (fmt || '{{amount}} €').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      if (k === 'amount_with_comma_separator') return sep(n, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return sep(n, 0, '.', ',');
      if (k === 'amount_no_decimals') return sep(n, 0, ',', '.');
      if (k === 'amount_with_apostrophe_separator') return sep(n, 2, "'", '.');
      return sep(n, 2, ',', '.');
    });
  }
  M.buybox = function (root) {
    $$('[data-ef-buybox]', root).forEach(function (box) {
      var dataEl = $('[data-ef-buybox-data]', box);
      if (!dataEl || box._efBuybox) return;
      box._efBuybox = true;
      var D; try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }
      var scope = box.closest('[data-ef-section]') || document;
      var idIn = $('[data-ef-buybox-id]', box), planIn = $('[data-ef-buybox-plan]', box);
      function money(c) { return efMoney(c, D.moneyFormat); }
      function opts() {
        var o = [];
        $$('[data-ef-buybox-opt]:checked', box).forEach(function (r) { o[+r.getAttribute('data-ef-buybox-opt')] = r.value; });
        return o;
      }
      function plan() { var r = $('[data-ef-buybox-planopt]:checked', box); return r ? r.value : ''; }
      function find(o) {
        return D.variants.filter(function (v) { return o.every(function (x, i) { return x == null || v.options[i] === x; }); })[0];
      }
      function priceOf(v, p) { return p && v.plans[p] != null ? v.plans[p] : v.price; }
      function update() {
        var o = opts(), v = find(o) || D.variants[0], p = plan();
        if (idIn) idIn.value = v.id;
        if (planIn) { planIn.value = p; planIn.disabled = !p; }
        // precio de cada cantidad (opción 0) con el plan elegido
        $$('[data-ef-buybox-optprice]', box).forEach(function (el) {
          var o2 = o.slice(); o2[0] = el.getAttribute('data-ef-buybox-optprice');
          var v2 = find(o2); if (v2) el.textContent = money(priceOf(v2, p));
        });
        $$('[data-ef-buybox-planprice]', box).forEach(function (el) {
          el.textContent = money(priceOf(v, el.getAttribute('data-ef-buybox-planprice')));
        });
        // "/mes" solo con entrega mensual: con "Entrega cada" en 2, 3 o 4 meses el precio va sin sufijo
        var freq = $('[data-ef-buybox-freq]', box), freqOpt = freq && freq.options[freq.selectedIndex];
        var monthly = !freq || (freqOpt && freqOpt.hasAttribute('data-ef-freq-monthly'));
        var price = priceOf(v, p), main = money(price) + (p && monthly ? (D.perMonth || '') : '');
        $$('[data-ef-buybox-price], [data-ef-buybox-barprice]', scope).forEach(function (el) { el.textContent = main; });
        // tachado (compare_at_price) y "−N %" de liquidación: solo compra única, sin planes (D.showWas)
        var was = D.showWas && !p && v.compare > price ? v.compare : 0;
        $$('[data-ef-buybox-was]', box).forEach(function (el) { el.hidden = !was; el.textContent = was ? money(was) : ''; });
        $$('[data-ef-buybox-off]', box).forEach(function (el) {
          el.hidden = !was || !D.offText;
          el.textContent = was && D.offText ? D.offText.replace('[N]', Math.round((was - price) * 100 / was)) : '';
        });
        var unit = $('[data-ef-buybox-unit]', box), num = parseInt(String(v.options[0]).replace(/[^0-9]/g, ''), 10);
        var unitTxt = D.perUnit && num > 1 ? money(Math.round(price / num)) + ' ' + D.perUnit + (D.unitsLabel ? ' · ' + num + ' ' + D.unitsLabel : '') : '';
        // "unitSave" (Ajustes por producto): ahorro por unidad frente a la variante más pequeña con las mismas
        // otras opciones, p. ej. "ahorras un [X] vs bote de [N] g" (bote de 90 g frente al de 30 g)
        if (unitTxt && D.unitSave) {
          var base = null;
          D.variants.forEach(function (w) {
            var n = parseInt(String(w.options[0]).replace(/[^0-9]/g, ''), 10);
            if (n > 1 && w.options.slice(1).join('|') === v.options.slice(1).join('|') && (!base || n < base.n)) base = { n: n, w: w };
          });
          if (base && num > base.n) {
            var pct = Math.round((1 - (price / num) / (priceOf(base.w, p) / base.n)) * 100);
            if (pct > 0) unitTxt += ' · ' + D.unitSave.replace('[X]', pct + ' %').replace('[N]', base.n);
          }
        }
        if (unit) unit.textContent = unitTxt;
        $$('[data-ef-buybox-microplan]', scope).forEach(function (el) { el.textContent = p ? D.microSub : D.microOnce; });
        $$('[data-ef-buybox-barqty]', scope).forEach(function (el) { el.textContent = String(v.options[0]).toLowerCase(); });
        $$('[data-ef-buybox-barshort]', scope).forEach(function (el) { el.textContent = p ? D.barSubShort : D.barOnceShort; });
        $$('[data-ef-buybox-barselect]', scope).forEach(function (el) { el.value = v.options[0]; });
        $$('[data-ef-buybox-barplan]', scope).forEach(function (el) {
          var r = p && $('[data-ef-buybox-planopt]:checked', box), save = r && r.closest('label').querySelector('.ef-pdp__save');
          el.textContent = p ? D.barSub + (save ? ' · ' + save.textContent.trim() : '') : D.barOnce;
        });
        var label = !v.available ? D.soldOut : (p ? D.ctaSubscribe : D.ctaOnce);
        $$('[data-ef-buybox-cta]', scope).forEach(function (el) { el.textContent = label; });
        $$('button[name="add"]', box).forEach(function (b) {
          if (v.available) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
        });
        $$('[data-ef-buybox-opt]', box).forEach(function (r) {
          var o2 = o.slice(); o2[+r.getAttribute('data-ef-buybox-opt')] = r.value;
          var v2 = find(o2); r.closest('label').classList.toggle('is-soldout', !v2 || !v2.available);
        });
        try {
          var u = new URL(window.location.href); u.searchParams.set('variant', v.id);
          // ?selling_plan= (si la URL lo trae) sigue al plan elegido: al volver a cargar no se recupera uno ya quitado
          if (u.searchParams.has('selling_plan')) { if (p) u.searchParams.set('selling_plan', p); else u.searchParams.delete('selling_plan'); }
          window.history.replaceState({}, '', u.toString());
        } catch (e) {}
        // imagen de la variante: galería (módulo gallery) y miniatura de la barra fija
        $$('[data-ef-gallery]', scope).forEach(function (g) { if (g._efGallery) g._efGallery.showVariant(v); });
        $$('[data-ef-buybox-barimg]', scope).forEach(function (img) {
          if (!img._efOrig) img._efOrig = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
          var src = v.thumb || v.img || img._efOrig.src, srcset = (v.thumb || v.img) ? null : img._efOrig.srcset;
          if (img.getAttribute('src') === src) return;
          if (srcset) img.setAttribute('srcset', srcset); else img.removeAttribute('srcset');
          img.setAttribute('src', src);
        });
        box._efVariant = v;
        box.dispatchEvent(new CustomEvent('ef:variant', { detail: { variant: v, plan: p }, bubbles: true }));
      }
      box.addEventListener('change', function (e) {
        // "Entrega cada" ([data-ef-buybox-freq], un plan por frecuencia): la tarjeta de suscripción pasa a ese plan y queda elegida
        if (e.target.matches('[data-ef-buybox-freq]')) {
          var card = e.target.closest('label'), r = card && $('[data-ef-buybox-planopt]', card);
          if (r) {
            r.value = e.target.value; r.checked = true;
            $$('[data-ef-buybox-planprice]', card).forEach(function (el) { el.setAttribute('data-ef-buybox-planprice', e.target.value); });
          }
          update();
        } else if (e.target.matches('[data-ef-buybox-opt], [data-ef-buybox-planopt]')) update();
      });
      // selector de cantidad de la barra fija: marca la opción equivalente de la caja
      $$('[data-ef-buybox-barselect]', scope).forEach(function (sel) {
        sel.addEventListener('change', function () {
          $$('[data-ef-buybox-opt="0"]', box).forEach(function (r) { if (r.value === sel.value) r.checked = true; });
          update();
        });
      });
      update();
      // al añadir, el contador del carrito rebota (Dawn vuelve a pintar la burbuja: se marca después)
      var cartEv = typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate; // const global de Dawn
      if (typeof subscribe === 'function' && cartEv && !window._efBump) {
        window._efBump = true;
        subscribe(cartEv, function () {
          setTimeout(function () {
            $$('.cart-count-bubble').forEach(function (b) { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); });
          }, 60);
        });
      }
    });
  };

  /* ======================================================================
     19 · REELS AMPLIABLES — modal a pantalla completa con sonido (referencia/home.html · .modal)
     <ul data-ef-reelmodal> … <button data-ef-reel-open><video …></video></button>
     Tocar un reel abre el modal con ese vídeo (con sonido y controles); ← → pasan de vídeo dentro del grupo;
     cierra con ×, Escape o tocando fuera. Bloquea el scroll y devuelve el foco al reel.
     ====================================================================== */
  var reelModal = null;
  function reelModalEl() {
    if (reelModal) return reelModal;
    var m = document.createElement('div');
    m.className = 'ef-rmodal';
    m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true'); m.setAttribute('aria-label', 'Vídeo de cliente');
    m.innerHTML = '<div class="ef-rmodal__box"><video playsinline controls></video>' +
      '<button type="button" class="ef-rmodal__close" aria-label="Cerrar">×</button>' +
      '<button type="button" class="ef-rmodal__nav ef-rmodal__nav--prev" aria-label="Vídeo anterior">←</button>' +
      '<button type="button" class="ef-rmodal__nav ef-rmodal__nav--next" aria-label="Vídeo siguiente">→</button></div>';
    document.body.appendChild(m);
    var mv = $('video', m), state = { items: [], cur: 0, last: null };
    function show(i) {
      var n = state.items.length; if (!n) return;
      state.cur = (i + n) % n;
      var src = $('video', state.items[state.cur]);
      if (!src) return;
      mv.src = src.currentSrc || src.getAttribute('src'); mv.poster = src.poster || ''; mv.muted = false;
      try { mv.currentTime = 0; } catch (e) {}
      var p = mv.play(); if (p) p.catch(function () { mv.muted = true; mv.play().catch(function () {}); });
      m.classList.toggle('is-single', n < 2);
    }
    function close() {
      if (!m.classList.contains('is-open')) return;
      m.classList.remove('is-open'); mv.pause();
      document.documentElement.classList.remove('ef-mmenu-lock');
      if (state.last) state.last.focus({ preventScroll: true });
    }
    $('.ef-rmodal__close', m).addEventListener('click', close);
    $('.ef-rmodal__nav--prev', m).addEventListener('click', function () { show(state.cur - 1); });
    $('.ef-rmodal__nav--next', m).addEventListener('click', function () { show(state.cur + 1); });
    m.addEventListener('click', function (e) { if (e.target === m) close(); });
    document.addEventListener('keydown', function (e) {
      if (!m.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') show(state.cur + 1);
      if (e.key === 'ArrowLeft') show(state.cur - 1);
    });
    reelModal = { el: m, open: function (items, i, from) {
      state.items = items; state.last = from; show(i);
      m.classList.add('is-open');
      document.documentElement.classList.add('ef-mmenu-lock'); // mismo bloqueo de scroll que el menú
      $('.ef-rmodal__close', m).focus({ preventScroll: true });
    } };
    return reelModal;
  }
  M.reelmodal = function (root) {
    $$('[data-ef-reelmodal]', root).forEach(function (group) {
      if (group._efReelModal) return;
      group._efReelModal = true;
      var items = $$('[data-ef-reel-open]', group);
      items.forEach(function (b, i) {
        b.addEventListener('click', function () { reelModalEl().open(items, i, b); });
      });
    });
  };

  /* ======================================================================
     20 · FILTROS QUE SE APLICAN SOLOS — PLP (filtros y orden de la colección)
     <form data-ef-autosubmit> … <select> / <input type="checkbox|radio|number"> …
     Al cambiar un campo se envía el formulario (GET con los filtros de Shopify), con 350 ms de margen para que
     varios cambios seguidos (p. ej. precio mín. y máx.) vayan en un solo envío. Sin JS funciona con su botón.
     ====================================================================== */
  M.autosubmit = function (root) {
    $$('form[data-ef-autosubmit]', root).forEach(function (f) {
      if (f._efAuto) return;
      f._efAuto = true;
      var t = null;
      f.addEventListener('change', function (e) {
        if (!e.target.name || !e.target.form) return; // los filtros locales (sin name/form) no recargan la página
        clearTimeout(t);
        t = setTimeout(function () {
          // sin campos vacíos en la URL (p. ej. precio sin rellenar)
          $$('input, select', f).forEach(function (el) { if (el.name && el.value === '') el.disabled = true; });
          if (f.requestSubmit) f.requestSubmit(); else f.submit();
        }, 350);
      });
    });
  };

  /* ======================================================================
     21 · DESPLEGABLES — <details> dentro de [data-ef-dropdowns] (filtros de la PLP)
     Uno abierto a la vez; se cierran al tocar fuera o con Escape.
     ====================================================================== */
  M.dropdowns = function (root) {
    $$('[data-ef-dropdowns]', root).forEach(function (box) {
      if (box._efDd) return;
      box._efDd = true;
      var all = $$('details', box);
      all.forEach(function (d) {
        d.addEventListener('toggle', function () { if (d.open) all.forEach(function (o) { if (o !== d) o.open = false; }); });
      });
      document.addEventListener('click', function (e) { if (!box.contains(e.target)) all.forEach(function (o) { o.open = false; }); });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        all.forEach(function (o) { if (o.open) { o.open = false; var s = $('summary', o); if (s) s.focus(); } });
      });
    });
  };

  /* ======================================================================
     22 · FILTROS LOCALES DE LA PLP — sections/ef-collection.liquid (sin filtros de Search & Discovery)
     <div data-ef-plpfilter> · inputs [data-ef-f="moment|size|sub|price"] · tarjetas [data-ef-f-item] con
     data-moment / data-size ("a|b|c"), data-sub ("once|sub|both") y data-price (céntimos).
     Dentro de un grupo las opciones suman (O); entre grupos restringen (Y). Actualiza la rejilla al momento,
     el contador [data-ef-f-count] (data-one / data-other con [N]), la insignia de cada filtro, las pastillas
     activas [data-ef-f-chips] con × y "Limpiar todo", y atenúa las opciones sin productos.
     Grupo "cat" (barra de categorías de la tienda, radios con value all|packs|capsulas|polvo y tarjetas con
     data-cat): su etiqueta lleva .is-on, no genera pastilla, no se atenúa, "Limpiar" no lo toca y se refleja en
     ?cat= de la URL (al cargar con ?cat=packs arranca filtrado).
     ====================================================================== */
  M.plpfilter = function (root) {
    $$('[data-ef-plpfilter]', root).forEach(function (box) {
      if (box._efPlp) return;
      var inputs = $$('[data-ef-f]', box), items = $$('[data-ef-f-item]', box);
      if (!inputs.length) return;
      box._efPlp = true;
      var counts = $$('[data-ef-f-count]', box), chips = $('[data-ef-f-chips]', box), list = $('[data-ef-f-chiplist]', box);
      var empty = $('[data-ef-f-empty]', box);
      function vals(el, k) { return (el.getAttribute('data-' + k) || '').split('|').map(function (x) { return x.trim(); }).filter(Boolean); }
      function match(it, g, v) {
        if (g === 'moment' || g === 'size') return vals(it, g).indexOf(v) > -1;
        if (g === 'cat') return v === 'all' || vals(it, 'cat').indexOf(v) > -1;
        if (g === 'sub') { var s = it.getAttribute('data-sub'); return v === 'all' || s === 'both' || s === v; }
        if (g === 'price') {
          var p = parseInt(it.getAttribute('data-price'), 10) || 0, r = v.split('-');
          var lo = parseFloat(r[0]) || 0, hi = r[1] === '' || r[1] == null ? Infinity : parseFloat(r[1]);
          // tramo [desde, hasta): un precio justo en el límite (20,00 €) cuenta solo en el tramo siguiente
          return p >= Math.round(lo * 100) && p < (hi === Infinity ? hi : Math.round(hi * 100));
        }
        return true;
      }
      function active() {
        var a = {};
        inputs.forEach(function (i) {
          if (!i.checked) return;
          var g = i.getAttribute('data-ef-f');
          if ((g === 'sub' || g === 'cat') && i.value === 'all') return;
          (a[g] = a[g] || []).push(i);
        });
        return a;
      }
      function passes(it, a, skip) {
        return Object.keys(a).every(function (g) {
          return g === skip || a[g].some(function (i) { return match(it, g, i.value); });
        });
      }
      function render() {
        var a = active(), n = 0;
        items.forEach(function (it) {
          var ok = passes(it, a);
          it.hidden = !ok;
          if (ok) { n++; it.classList.add('is-in'); }
        });
        counts.forEach(function (count) {
          var tpl = n === 1 ? count.getAttribute('data-one') : count.getAttribute('data-other');
          if (tpl) count.textContent = tpl.replace('[N]', n);
        });
        if (empty) empty.hidden = n > 0;
        // opciones sin productos (con el resto de filtros aplicados)
        inputs.forEach(function (i) {
          var g = i.getAttribute('data-ef-f');
          if (g === 'cat') { i.closest('label').classList.toggle('is-on', i.checked); return; }
          var any = (g === 'sub' && i.value === 'all') || items.some(function (it) { return passes(it, a, g) && match(it, g, i.value); });
          i.closest('label').classList.toggle('is-off', !any && !i.checked);
          i.disabled = !any && !i.checked;
        });
        $$('[data-ef-f-dd]', box).forEach(function (dd) {
          var g = dd.getAttribute('data-ef-f-dd'), b = $('[data-ef-f-badge]', dd), k = (a[g] || []).length;
          dd.classList.toggle('is-active', k > 0);
          if (b) { b.hidden = !k || g === 'sub' || g === 'price'; b.textContent = k; }
        });
        if (chips && list) {
          list.innerHTML = '';
          Object.keys(a).forEach(function (g) {
            if (g === 'cat') return;
            a[g].forEach(function (i) {
              var c = document.createElement('button');
              c.type = 'button'; c.className = 'ef-plp__chip';
              c.innerHTML = '<span></span><span aria-hidden="true">×</span>';
              c.firstChild.textContent = i.getAttribute('data-ef-f-label');
              c.setAttribute('aria-label', 'Quitar filtro: ' + i.getAttribute('data-ef-f-label'));
              c.addEventListener('click', function () { i.checked = false; render(); });
              list.appendChild(c);
            });
          });
          chips.hidden = !Object.keys(a).filter(function (g) { return g !== 'cat'; }).length;
        }
      }
      function clear(g) {
        inputs.forEach(function (i) {
          var ig = i.getAttribute('data-ef-f');
          if ((g === 'all' && ig !== 'cat') || ig === g) i.checked = false;
        });
        render();
      }
      var cats = inputs.filter(function (i) { return i.getAttribute('data-ef-f') === 'cat'; });
      if (cats.length) {
        var q = (location.search.match(/[?&]cat=([^&]+)/) || [])[1];
        cats.forEach(function (i) { if (q && i.value === decodeURIComponent(q)) i.checked = true; });
      }
      box.addEventListener('change', function (e) {
        if (!e.target.matches('[data-ef-f]')) return;
        render();
        if (e.target.getAttribute('data-ef-f') === 'cat' && window.history && history.replaceState) {
          var u = new URL(location.href);
          if (e.target.value === 'all') u.searchParams.delete('cat'); else u.searchParams.set('cat', e.target.value);
          history.replaceState(history.state, '', u.toString());
        }
      });
      $$('[data-ef-f-clear]', box).forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); clear(b.getAttribute('data-ef-f-clear')); });
      });
      render();
    });
  };

  /* ======================================================================
     23 · HOJA INFERIOR (mobile) — filtros de la PLP
     [data-ef-sheet] (con id) · lo abre [data-ef-sheet-open][aria-controls=id] · lo cierran [data-ef-sheet-close],
     Escape, deslizar hacia abajo desde [data-ef-sheet-grab] o pasar a ≥ 750 (allí la hoja es la fila de filtros).
     Al abrir despliega el primer grupo si no hay ninguno abierto; bloquea el scroll de la página.
     ====================================================================== */
  M.sheet = function (root) {
    var mq = window.matchMedia('(max-width: 749px)');
    $$('[data-ef-sheet-open]', root).forEach(function (btn) {
      if (btn._efSheet) return;
      var sheet = document.getElementById(btn.getAttribute('aria-controls'));
      if (!sheet) return;
      btn._efSheet = true;
      var scope = btn.closest('form') || document;
      var closers = $$('[data-ef-sheet-close]', scope);
      function set(open) {
        sheet.classList.toggle('is-open', open);
        closers.forEach(function (c) { if (c.classList.contains('ef-plp__scrim') || c.hasAttribute('data-ef-sheet-scrim')) c.classList.toggle('is-open', open); });
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.classList.toggle('ef-sheet-lock', open);
      }
      function open() {
        if (!mq.matches) return;
        var dd = $$('details', sheet);
        if (dd.length && !dd.some(function (d) { return d.open; })) dd[0].open = true;
        set(true);
        var x = $('[data-ef-sheet-close]', sheet);
        if (x) x.focus({ preventScroll: true });
      }
      function close() {
        if (!sheet.classList.contains('is-open')) return;
        set(false);
        btn.focus({ preventScroll: true });
      }
      btn.addEventListener('click', open);
      closers.forEach(function (c) { c.addEventListener('click', function (e) { e.preventDefault(); close(); }); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      (mq.addEventListener ? mq.addEventListener('change', function () { if (!mq.matches) set(false); }) : 0);
      var grab = $('[data-ef-sheet-grab]', sheet), y0 = null;
      if (grab) {
        grab.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; sheet.classList.add('is-drag'); }, { passive: true });
        grab.addEventListener('touchmove', function (e) {
          if (y0 == null) return;
          var dy = Math.max(0, e.touches[0].clientY - y0);
          sheet.style.transform = 'translateY(' + dy + 'px)';
        }, { passive: true });
        grab.addEventListener('touchend', function (e) {
          if (y0 == null) return;
          var dy = e.changedTouches[0].clientY - y0;
          y0 = null; sheet.classList.remove('is-drag'); sheet.style.transform = '';
          if (dy > 70) close();
        });
      }
    });
  };

  /* ======================================================================
     24 · TEXTO QUE ENTRA A TROZOS — referencia/manifiesto.html [data-split]
     <h1 data-ef-split="chars" data-ef-split-step="55" data-ef-split-base="200">Manifiesto</h1>
     <p data-ef-split="words" data-ef-split-step="28" data-ef-split-base="750">…</p>
     Parte el texto en letras o palabras (spans .ef-split__u con --i); el CSS (bloque 31) las hace subir una tras otra
     al cargar. El texto completo queda en aria-label. Con reduce no se parte.
     ====================================================================== */
  M.split = function (root) {
    if (reduce) return;
    $$('[data-ef-split]', root).forEach(function (el) {
      if (el.querySelector('.ef-split__u')) return;
      var txt = el.textContent.trim(), mode = el.getAttribute('data-ef-split');
      if (!txt) return;
      var step = el.getAttribute('data-ef-split-step'), base = el.getAttribute('data-ef-split-base');
      if (step) el.style.setProperty('--ef-split-step', step + 'ms');
      if (base) el.style.setProperty('--ef-split-base', base + 'ms');
      el.setAttribute('aria-label', txt);
      el.textContent = '';
      var parts = mode === 'chars' ? Array.from(txt) : txt.split(/\s+/);
      parts.forEach(function (p, i) {
        var s = document.createElement('span');
        s.className = 'ef-split__u'; s.setAttribute('aria-hidden', 'true'); s.style.setProperty('--i', i);
        s.textContent = p;
        el.appendChild(s);
        if (mode !== 'chars' && i < parts.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.classList.add('is-split');
    });
  };

  /* ======================================================================
     25 · CONTENEDOR EN PANTALLA — una sola vez pone .is-in (data-ef-inview="0.35" = umbral)
     Para coreografías que hace el CSS de la sección (línea que se dibuja, aros, puntos con retardo) sin el fundido
     de data-ef-reveal. Los [data-ef-count] de dentro arrancan con este contenedor (módulo 8).
     ====================================================================== */
  M.inview = function (root) {
    $$('[data-ef-inview]', root).forEach(function (el) {
      if (reduce) { el.classList.add('is-in'); return; }
      var th = parseFloat(el.getAttribute('data-ef-inview')) || 0.3;
      onView(el, function (t, vis) { if (vis) t.classList.add('is-in'); }, th, true);
    });
  };

  /* ======================================================================
     26 · PARALLAX AL SCROLL — <img data-ef-parallax="0.12">
     Se desplaza hacia abajo k px por cada px de scroll desde que entra en pantalla (propiedad `translate`, así no
     pisa las animaciones de `transform`). Solo mientras se ve. Sin reduce.
     ====================================================================== */
  M.parallax = function (root) {
    if (reduce) return;
    $$('[data-ef-parallax]', root).forEach(function (el) {
      var k = parseFloat(el.getAttribute('data-ef-parallax')) || 0.12, ticking = false;
      function set() {
        ticking = false;
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var start = Math.max(0, r.top + window.scrollY - window.innerHeight);
        el.style.translate = '0 ' + (Math.max(0, window.scrollY - start) * k).toFixed(1) + 'px';
      }
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(set); } }, { passive: true });
      set();
    });
  };

  /* ======================================================================
     27 · SIGUE AL RATÓN — <div data-ef-follow="-18"> (stickers)
     Se corre hasta ±k/2 px según la posición del puntero en la ventana. Va en un envoltorio: así el flotar de
     data-ef-float (CSS 25) del hijo no se pisa. Sin reduce ni en táctil.
     ====================================================================== */
  M.follow = function (root) {
    if (reduce) return;
    var els = $$('[data-ef-follow]', root);
    if (!els.length) return;
    window.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      var x = e.clientX / window.innerWidth - 0.5, y = e.clientY / window.innerHeight - 0.5;
      els.forEach(function (el) {
        var k = parseFloat(el.getAttribute('data-ef-follow')) || 0;
        el.style.transform = 'translate(' + (x * k).toFixed(1) + 'px,' + (y * k).toFixed(1) + 'px)';
      });
    }, { passive: true });
  };

  /* ======================================================================
     28 · SE ENCIENDE EL QUE ESTÁS LEYENDO — <ol data-ef-spotlight> <li data-ef-spotlight-item>
     Pone .is-on en el ítem que cruza la franja central de la pantalla (12 % del alto). El CSS lo combina con :hover.
     ====================================================================== */
  M.spotlight = function (root) {
    if (!('IntersectionObserver' in window)) return;
    $$('[data-ef-spotlight]', root).forEach(function (box) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { e.target.classList.toggle('is-on', e.isIntersecting); });
      }, { rootMargin: '-44% 0px -44% 0px' });
      $$('[data-ef-spotlight-item]', box).forEach(function (it) { io.observe(it); });
    });
  };

  /* ======================================================================
     29 · ARRASTRAR Y SOLTAR — referencia/profesionales.html (.vcard de la órbita)
     <div data-ef-drag data-ef-drag-min="761">  · se arrastra con el ratón (o el dedo) y al soltar vuelve a su sitio
     Gira hasta ±8° según el desplazamiento. Clase .is-dragging mientras se arrastra (el CSS 35 quita la transición).
     Por debajo de data-ef-drag-min px de ancho (750 por defecto) no hace nada: la página se desplaza normal.
     ====================================================================== */
  M.drag = function (root) {
    $$('[data-ef-drag]', root).forEach(function (el) {
      if (el._efDrag) return;
      el._efDrag = true;
      var min = parseInt(el.getAttribute('data-ef-drag-min'), 10) || 750, sx = 0, sy = 0, on = false;
      el.addEventListener('pointerdown', function (e) {
        if (window.innerWidth < min || e.button > 0) return;
        on = true; sx = e.clientX; sy = e.clientY;
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
        el.classList.add('is-dragging');
      });
      el.addEventListener('pointermove', function (e) {
        if (!on) return;
        var dx = e.clientX - sx, dy = e.clientY - sy;
        el.style.transform = 'translate(' + dx + 'px,' + dy + 'px) rotate(' + Math.max(-8, Math.min(8, dx / 25)) + 'deg)';
      });
      function end() { if (!on) return; on = false; el.classList.remove('is-dragging'); el.style.transform = ''; }
      el.addEventListener('pointerup', end);
      el.addEventListener('pointercancel', end);
    });
  };

  /* ======================================================================
     30 · FORMULARIO DE PRESUPUESTO — referencia/profesionales.html (#rfq) · sections/ef-prof-quote.liquid
     <form data-ef-quote> (el {% form 'contact' %} de Shopify: se envía de verdad si todo está bien)
     - Formatos: [data-ef-quote-fmt="polvo"] con su casilla [data-ef-quote-fmt-toggle]. Marcada → .is-on (despliega el
       cuerpo) y habilita los campos de dentro; desmarcada → se pliega y sus campos van deshabilitados (no se envían).
       Un campo con data-ef-quote-off queda siempre deshabilitado (p. ej. una variedad "pronto").
     - Interruptor: [data-ef-quote-sw="etiqueta"] con [data-ef-quote-sw-toggle] → .is-on.
     - Validación al salir del campo y al enviar: [data-ef-quote-rule="required|email|tel"] + data-ef-quote-msg; el error
       va en [data-ef-quote-err="<id del campo>"]. Grupo: [data-ef-quote-group] (al menos una casilla marcada) con su
       [data-ef-quote-err="<data-ef-quote-group>"]. Con errores no se envía: foco y scroll al primero.
     - CTA de otras secciones: [data-ef-pick="polvo"], "capsulas", "polvo capsulas", "etiqueta"… deja marcados esos
       formatos (y desmarca los demás) o enciende el interruptor, y los resalta (.is-flash). El enlace hace el resto (#ancla).
     ====================================================================== */
  M.quote = function (root) {
    $$('form[data-ef-quote]', root).forEach(function (form) {
      if (form._efQuote) return;
      form._efQuote = true;
      form.noValidate = true;
      function syncFmt(box) {
        var t = $('[data-ef-quote-fmt-toggle]', box), on = !!(t && t.checked);
        box.classList.toggle('is-on', on);
        $$('input, select, textarea', box).forEach(function (i) {
          if (i === t) return;
          i.disabled = i.hasAttribute('data-ef-quote-off') || !on;
        });
      }
      function syncSw(box) { var t = $('[data-ef-quote-sw-toggle]', box); box.classList.toggle('is-on', !!(t && t.checked)); }
      function errEl(key) { return $('[data-ef-quote-err="' + key + '"]', form); }
      function setErr(key, msg, input) {
        var e = errEl(key);
        if (e) e.textContent = msg;
        var field = input ? input.closest('[data-ef-quote-field]') : null;
        if (field) field.classList.toggle('is-bad', !!msg);
        if (input) {
          if (msg) { input.setAttribute('aria-invalid', 'true'); if (e && e.id) input.setAttribute('aria-describedby', e.id); }
          else { input.removeAttribute('aria-invalid'); input.removeAttribute('aria-describedby'); }
        }
      }
      function check(input) {
        var rule = input.getAttribute('data-ef-quote-rule'), v = input.value.trim(), ok = true;
        if (rule === 'email') ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
        else if (rule === 'tel') ok = v.replace(/\D/g, '').length >= 9;
        else ok = v !== '';
        var msg = ok ? '' : (input.getAttribute('data-ef-quote-msg') || '');
        setErr(input.id, msg, input);
        return ok;
      }
      function checkGroup(g) {
        var ok = $$('input:checked', g).some(function (i) { return !i.disabled; });
        setErr(g.getAttribute('data-ef-quote-group'), ok ? '' : (g.getAttribute('data-ef-quote-msg') || ''));
        return ok;
      }
      $$('[data-ef-quote-fmt]', form).forEach(function (box) {
        var t = $('[data-ef-quote-fmt-toggle]', box);
        if (t) t.addEventListener('change', function () {
          syncFmt(box);
          var g = box.closest('[data-ef-quote-group]'); if (g) checkGroup(g);
        });
        syncFmt(box);
      });
      $$('[data-ef-quote-sw]', form).forEach(function (box) {
        var t = $('[data-ef-quote-sw-toggle]', box);
        if (t) t.addEventListener('change', function () { syncSw(box); });
        syncSw(box);
      });
      $$('[data-ef-quote-rule]', form).forEach(function (input) {
        input.addEventListener('blur', function () { if (input.value) check(input); });
        input.addEventListener('input', function () { var e = errEl(input.id); if (e && e.textContent) check(input); });
      });
      form.addEventListener('submit', function (e) {
        var first = null;
        $$('[data-ef-quote-rule]', form).forEach(function (input) { if (!check(input) && !first) first = input; });
        $$('[data-ef-quote-group]', form).forEach(function (g) {
          if (!checkGroup(g) && !first) first = $('input', g);
        });
        if (!first) return; // todo bien: envío normal del formulario de Shopify
        e.preventDefault();
        first.focus({ preventScroll: true });
        first.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      });
      form._efQuoteApi = { syncFmt: syncFmt, syncSw: syncSw, checkGroup: checkGroup };
    });
    if (window._efPickBound) return;
    window._efPickBound = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('[data-ef-pick]');
      if (!a) return;
      var keys = (a.getAttribute('data-ef-pick') || '').split(/[\s,]+/).filter(Boolean);
      $$('form[data-ef-quote]').forEach(function (form) {
        var api = form._efQuoteApi;
        if (!api) return;
        var fmts = $$('[data-ef-quote-fmt]', form), touched = [];
        var picked = fmts.filter(function (b) { return keys.indexOf(b.getAttribute('data-ef-quote-fmt')) > -1; });
        if (picked.length) fmts.forEach(function (b) {
          var t = $('[data-ef-quote-fmt-toggle]', b), on = picked.indexOf(b) > -1;
          if (t) t.checked = on;
          api.syncFmt(b);
          if (on) touched.push(b);
        });
        $$('[data-ef-quote-sw]', form).forEach(function (b) {
          if (keys.indexOf(b.getAttribute('data-ef-quote-sw')) < 0) return;
          var t = $('[data-ef-quote-sw-toggle]', b);
          if (t) t.checked = true;
          api.syncSw(b);
          touched.push(b);
        });
        $$('[data-ef-quote-group]', form).forEach(function (g) { if (picked.length) api.checkGroup(g); });
        touched.forEach(function (b) { b.classList.remove('is-flash'); void b.offsetWidth; b.classList.add('is-flash'); });
      });
    });
  };

  /* ======================================================================
     31 · BUSCADOR — panel que baja desde arriba (sections/ef-search.liquid + sections/ef-search-results.liquid)
     <div id="ef-search" data-ef-search data-ef-search-url="/search/suggest" data-ef-search-results="ef-search-results"
          data-ef-search-page="/search"> … <form data-ef-search-form><input data-ef-search-input> …
     - Lo abren la lupa del header de Dawn (el <summary> de details-modal.header__search: su modal no se abre) y
       cualquier [data-ef-search-open] (la lupa del menú mobile: cierra el menú y el foco vuelve luego a la hamburguesa).
       Si la sección no está, la búsqueda de Dawn funciona como siempre.
     - Cierra con [data-ef-search-close] (Cerrar y el velo), Escape o un enlace a la misma página; devuelve el foco al ícono.
       Bloquea el scroll (html.ef-search-lock), inert + aria-hidden cerrado, el foco no sale del panel (Tab).
     - Estados [data-ef-search-state]: "empty" (campo vacío) · "results" · "none" (Shopify no devolvió nada).
       Al escribir (200 ms de margen, la petición anterior se cancela) pide la Predictive Search API con
       section_id=ef-search-results y reparte sus [data-ef-search-slot] (suggestions · pages · products) en los del panel.
       A las sugerencias se suman las búsquedas populares del panel ([data-ef-search-pop]) que contienen el término;
       [data-ef-search-hl] → la coincidencia en negrita (sin tildes ni mayúsculas). Máximo 4.
     - ↑ ↓ recorren las sugerencias (la primera sale resaltada) e Intro abre la elegida; sin flechas, Intro busca lo
       escrito en /search (solo productos, ?options[prefix]=last como la predictiva).
     - Pastillas [data-ef-search-q="término"]: lo escriben en el campo y buscan al momento. [data-ef-search-clear] borra.
     - Contadores: [data-ef-search-count] y [data-ef-search-all] con data-one / data-many ("[n]" = productos).
       [data-ef-search-echo] recibe el término (estado sin resultados). Cargando: .is-loading en el panel.
     - Mobile (< 750, matchMedia): el campo toma data-ef-search-ph-m como placeholder y el grupo de páginas pasa al final
       del estado "results" (debajo de los productos, también en el orden del foco); al volver a ≥ 750 vuelve a su sitio.
       El foco al input ocurre dentro del mismo toque que abre (iOS saca el teclado).
     ====================================================================== */
  var searchFrom = null;
  // minúsculas y sin tildes, carácter a carácter (mismo largo que el original: sirve para cortar el texto)
  function searchNorm(s) {
    return (s || '').split('').map(function (c) {
      return (c.normalize ? c.normalize('NFD').replace(/[̀-ͯ]/g, '') : c).toLowerCase().charAt(0) || c;
    }).join('');
  }
  function searchMark(el, q) {
    var text = el.textContent, nq = searchNorm(q.trim()), i = nq ? searchNorm(text).indexOf(nq) : -1;
    if (i < 0) return;
    el.textContent = '';
    el.appendChild(document.createTextNode(text.slice(0, i)));
    var b = document.createElement('b');
    b.textContent = text.slice(i, i + nq.length);
    el.appendChild(b);
    el.appendChild(document.createTextNode(text.slice(i + nq.length)));
  }
  function searchOpen(panel, from) {
    if (panel.classList.contains('is-open')) return;
    searchFrom = from || document.activeElement;
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    document.documentElement.classList.add('ef-search-lock');
    if (from && from.matches && from.matches('summary')) from.setAttribute('aria-expanded', 'true');
    var input = $('[data-ef-search-input]', panel);
    if (input) { input.focus({ preventScroll: true }); try { input.select(); } catch (e) {} }
  }
  function searchClose() {
    var panel = document.getElementById('ef-search');
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
    document.documentElement.classList.remove('ef-search-lock');
    var from = searchFrom;
    searchFrom = null;
    if (from && from.isConnected) {
      if (from.matches && from.matches('summary')) from.setAttribute('aria-expanded', 'false');
      if (from.focus) from.focus({ preventScroll: true });
    }
  }
  M.search = function (root) {
    $$('[data-ef-search]', root).forEach(function (panel) {
      if (panel._efSearch) return;
      panel._efSearch = true;
      panel.setAttribute('inert', '');
      var input = $('[data-ef-search-input]', panel), form = $('[data-ef-search-form]', panel);
      var field = $('[data-ef-search-field]', panel), body = $('[data-ef-search-body]', panel);
      if (!input || !form) return;
      var base = panel.getAttribute('data-ef-search-url') || '/search/suggest';
      var sectionId = panel.getAttribute('data-ef-search-results') || 'ef-search-results';
      var page = panel.getAttribute('data-ef-search-page') || '/search';
      var states = $$('[data-ef-search-state]', panel);
      var resultsState = $('[data-ef-search-state="results"]', panel);
      var slot = function (n) { return $('[data-ef-search-slot="' + n + '"]', panel); };
      var group = function (n) { return $('[data-ef-search-group="' + n + '"]', panel); };
      var count = $('[data-ef-search-count]', panel), all = $('[data-ef-search-all]', panel);
      var lupa = $('.ef-search__lupa svg', panel);
      var timer = null, ctrl = null, idx = -1, armed = false, lastQ = null;

      // Mobile (< 750): texto del campo corto (data-ef-search-ph-m) y las páginas al final, debajo de los productos
      var mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
      var ph = input.getAttribute('placeholder') || '', phM = input.getAttribute('data-ef-search-ph-m');
      var pagesG = group('pages'), pagesHome = pagesG && pagesG.parentNode;
      function layout() {
        var m = !!(mq && mq.matches);
        if (phM) input.setAttribute('placeholder', m ? phM : ph);
        var to = m ? resultsState : pagesHome;
        if (pagesG && to && pagesG.parentNode !== to) to.appendChild(pagesG);
      }
      layout();
      if (mq && mq.addEventListener) mq.addEventListener('change', layout);
      else if (mq && mq.addListener) mq.addListener(layout);

      function opts() { var s = slot('suggestions'); return s ? $$('[data-ef-search-opt]', s) : []; }
      function setActive(i, arm) {
        var list = opts();
        idx = i; armed = !!arm && i > -1;
        list.forEach(function (o, k) {
          o.classList.toggle('is-active', k === i);
          o.setAttribute('aria-selected', k === i ? 'true' : 'false');
        });
        if (armed && list[i]) input.setAttribute('aria-activedescendant', list[i].id);
        else input.removeAttribute('aria-activedescendant');
      }
      function setState(name) {
        states.forEach(function (s) {
          var on = s.getAttribute('data-ef-search-state') === name;
          if (on && s.hidden) { s.hidden = false; s.classList.remove('is-swap'); void s.offsetWidth; s.classList.add('is-swap'); }
          if (!on) s.hidden = true;
        });
        input.setAttribute('aria-expanded', name === 'results' && opts().length ? 'true' : 'false');
      }
      function searchUrl(q) { return page + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last'; }
      function label(el, n) { return (el.getAttribute(n === 1 ? 'data-one' : 'data-many') || '').replace('[n]', n); }
      function move(from, to) { to.textContent = ''; if (from) while (from.firstChild) to.appendChild(from.firstChild); }

      function render(res, q) {
        var n = parseInt(res.getAttribute('data-products'), 10) || 0;
        if (!(parseInt(res.getAttribute('data-total'), 10) || 0)) {
          $$('[data-ef-search-echo]', panel).forEach(function (e) { e.textContent = q; });
          setActive(-1);
          setState('none');
          return;
        }
        // Sugerencias: las de Shopify y, detrás, las búsquedas populares que contienen lo escrito; sin repetir
        var seen = {}, nq = searchNorm(q), items = [];
        var got = $('[data-ef-search-slot="suggestions"]', res);
        (got ? $$('li', got) : []).forEach(function (li) {
          var a = $('[data-ef-search-opt]', li), k = searchNorm(a ? a.getAttribute('data-ef-search-key') || a.textContent.trim() : '');
          if (!a || seen[k]) return;
          seen[k] = 1; items.push(li);
        });
        $$('[data-ef-search-pop]', panel).forEach(function (p) {
          var term = p.getAttribute('data-ef-search-q') || p.textContent.trim(), txt = p.textContent.trim();
          var k = searchNorm(term);
          if (seen[k] || seen[searchNorm(txt)] || (k.indexOf(nq) < 0 && searchNorm(txt).indexOf(nq) < 0)) return;
          seen[k] = 1;
          var li = document.createElement('li'), a = document.createElement('a'), ico = document.createElement('span');
          var s = document.createElement('span');
          li.setAttribute('role', 'presentation');
          a.className = 'ef-search__sug'; a.href = searchUrl(term);
          a.setAttribute('role', 'option'); a.setAttribute('data-ef-search-opt', '');
          ico.className = 'ef-search__sug-ico'; ico.setAttribute('aria-hidden', 'true');
          if (lupa) ico.appendChild(lupa.cloneNode(true));
          s.setAttribute('data-ef-search-hl', ''); s.textContent = txt;
          a.appendChild(ico); a.appendChild(s); li.appendChild(a);
          items.push(li);
        });
        var sugs = slot('suggestions');
        sugs.textContent = '';
        items.slice(0, 4).forEach(function (li, i) {
          $('[data-ef-search-opt]', li).id = 'ef-search-opt-' + i;
          $$('[data-ef-search-hl]', li).forEach(function (s) { searchMark(s, q); });
          sugs.appendChild(li);
        });
        if (group('suggestions')) group('suggestions').hidden = !sugs.children.length;
        // Páginas y productos tal cual llegan de Shopify
        move($('[data-ef-search-slot="pages"]', res), slot('pages'));
        if (group('pages')) group('pages').hidden = !$('li', slot('pages'));
        var grid = slot('products');
        move($('[data-ef-search-slot="products"]', res), grid);
        var cols = Math.max(3, Math.min(n, 4));
        grid.style.setProperty('--n', cols);
        grid.classList.toggle('is-3', cols === 3);
        if (count) count.textContent = label(count, n);
        if (all) {
          all.hidden = !n;
          all.href = searchUrl(q);
          var t = $('[data-ef-search-all-txt]', all);
          if (t) t.textContent = label(all, n);
        }
        setState('results');
        setActive(sugs.children.length ? 0 : -1, false);
        if (!resultsState.classList.contains('is-swap')) return;
        grid.classList.remove('is-swap'); void grid.offsetWidth; grid.classList.add('is-swap');
      }

      function done(c) {
        if (c !== ctrl) return;
        ctrl = null;
        panel.classList.remove('is-loading');
        if (body) body.removeAttribute('aria-busy');
      }
      function load(q) {
        if (ctrl && ctrl.abort) ctrl.abort();
        var c = ctrl = window.AbortController ? new AbortController() : {};
        panel.classList.add('is-loading');
        if (body) body.setAttribute('aria-busy', 'true');
        var url = base + '?q=' + encodeURIComponent(q) +
          '&resources%5Btype%5D=product,page,query,collection&resources%5Blimit%5D=10&resources%5Blimit_scope%5D=each' +
          '&section_id=' + encodeURIComponent(sectionId);
        fetch(url, c.signal ? { signal: c.signal } : {})
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
          .then(function (html) {
            if (c !== ctrl) return;
            var res = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ef-search-res]');
            if (!res) throw new Error('respuesta sin [data-ef-search-res]');
            lastQ = q;
            render(res, q);
          })
          .catch(function (err) { if (!err || err.name !== 'AbortError') console.warn('[ef] buscador', err); })
          .then(function () { done(c); });
      }
      function run(now) {
        var q = input.value.trim();
        clearTimeout(timer);
        if (field) field.classList.toggle('is-filled', input.value !== '');
        if (!q) {
          if (ctrl) { ctrl.abort && ctrl.abort(); done(ctrl); }
          lastQ = null;
          setActive(-1);
          setState('empty');
          return;
        }
        if (q === lastQ) { if (ctrl) { ctrl.abort && ctrl.abort(); done(ctrl); } return; }
        if (now) load(q); else timer = setTimeout(function () { load(q); }, 200);
      }

      input.addEventListener('input', function () { run(false); });
      input.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var list = opts();
        if (resultsState.hidden || !list.length) return;
        e.preventDefault();
        var d = e.key === 'ArrowDown' ? 1 : -1;
        setActive(!armed ? (d > 0 ? 0 : list.length - 1) : (idx + d + list.length) % list.length, true);
        list[idx].scrollIntoView({ block: 'nearest' });
      });
      form.addEventListener('submit', function (e) {
        var list = opts();
        if (armed && list[idx] && !resultsState.hidden) { e.preventDefault(); window.location.href = list[idx].href; return; }
        if (!input.value.trim()) { e.preventDefault(); input.focus(); }
      });
      var sugs = slot('suggestions');
      if (sugs) sugs.addEventListener('mousemove', function (e) {
        var o = e.target.closest('[data-ef-search-opt]');
        var i = opts().indexOf(o);
        if (o && i !== idx) setActive(i, armed);
      });
      panel.addEventListener('click', function (e) {
        if (e.target.closest('[data-ef-search-close]')) { searchClose(); return; }
        if (e.target.closest('[data-ef-search-clear]')) { input.value = ''; run(true); input.focus(); return; }
        var pill = e.target.closest('[data-ef-search-q]');
        if (pill && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
          e.preventDefault();
          input.value = pill.getAttribute('data-ef-search-q');
          run(true);
          input.focus({ preventScroll: true });
          return;
        }
        // Enlace a la misma página (p. ej. un ancla): se cierra para que se vea el destino
        var a = e.target.closest('a[href]');
        if (a && a.pathname === window.location.pathname && a.hash) searchClose();
      });
      // El foco no sale del panel mientras está abierto
      panel.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var f = $$('a[href], button, input:not([type="hidden"])', panel).filter(function (el) {
          return !el.disabled && el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
        });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    });
    if (window._efSearchBound) return;
    window._efSearchBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target.closest('details-modal.header__search summary, [data-ef-search-open]');
      var panel = document.getElementById('ef-search');
      if (!t || !panel || !panel._efSearch) return;
      if (t.matches('a') && (e.metaKey || e.ctrlKey || e.shiftKey)) return; // abrir /search en otra pestaña
      e.preventDefault();
      e.stopImmediatePropagation(); // ni el modal de Dawn ni el cierre del menú mobile por enlace
      var back = t;
      if (t.closest('#ef-mmenu')) { back = mmenuFrom || t; mmenuClose(); }
      searchOpen(panel, back);
    }, true);
    document.addEventListener('keydown', function (e) {
      var panel = document.getElementById('ef-search');
      if (e.key === 'Escape' && panel && panel.classList.contains('is-open')) { e.preventDefault(); searchClose(); }
    });
    // Editor de temas: al elegir la sección se abre; al soltarla o quitarla, se cierra
    document.addEventListener('shopify:section:select', function (e) {
      var p = e.target && e.target.querySelector && e.target.querySelector('[data-ef-search]');
      if (p) { if (!p._efSearch) M.search(e.target); searchOpen(p, null); }
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-ef-search]')) searchClose();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-ef-search]')) {
        searchClose();
        document.documentElement.classList.remove('ef-search-lock');
      }
    });
  };

  /* ======================================================================
     32 · CARRITO — cajón (snippets/cart-drawer.liquid) y página /cart (sections/main-cart-items.liquid)
     Lo de Dawn sigue igual: cart-drawer.js abre/cierra (velo, Esc con el foco dentro, bloqueo de scroll) y cart.js
     cambia cantidades, elimina y vuelve a pintar con la Section Rendering API. Este módulo solo añade:
     - [data-ef-cart-add="<id variante>"] ("Añadir +" de los recomendados): /cart/add.js (compra única, 1 ud.) y vuelve a
       pintar el cajón (drawer.renderContents, como product-form.js) o la página (#main-cart-items .js-contents) y la
       burbuja del header, sin recargar. Botón .is-loading mientras tanto; error → .is-error + mensaje en #…CartErrors.
     - [data-ef-cart-plan="<selling plan>"] + data-ef-cart-line (key) + data-ef-cart-qty ("Cambiar" a suscripción):
       /cart/change.js con selling_plan y vuelve a pintar la página.
     - [data-ef-cart-close]: cierra el cajón · [data-ef-cart-open]: lo abre (si no hay cajón, el enlace va a /cart).
     - Franja [data-ef-cart-added]: aparece tras un añadido (product-form o "Añadir +") y a los 3,5 s se pliega.
     - Barra [data-ef-cart-bar="0–100"]: se llena desde el valor anterior (y desde 0 al abrir el cajón).
     - Al abrir: .is-entering en <cart-drawer> ~1 s (escalonado, CSS 38). Foco: Tab no sale del panel aunque se haya
       vuelto a pintar (la trampa de Dawn se queda con los elementos viejos); Esc cierra aunque el foco esté fuera.
     Publica PUB_SUB_EVENTS.cartUpdate (source 'cart-items', efSource 'ef-cart') para el rebote del contador (módulo 18).
     ====================================================================== */
  var cartLastP = null;
  function cartBars(scope, from) {
    $$('[data-ef-cart-bar]', scope).forEach(function (bar) {
      var fill = bar.firstElementChild, p = parseFloat(bar.getAttribute('data-ef-cart-bar')) || 0;
      if (!fill) return;
      var f = from != null ? from : (cartLastP != null ? cartLastP : 0);
      cartLastP = p;
      if (reduce || f === p) { fill.style.width = p + '%'; return; }
      fill.style.transition = 'none';
      fill.style.width = f + '%';
      void fill.offsetWidth;
      fill.style.transition = '';
      requestAnimationFrame(function () { fill.style.width = p + '%'; });
    });
  }
  function cartDrawer() { return document.querySelector('cart-drawer'); }
  function cartSectionHTML(html, sel) {
    var el = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
    return el ? el.innerHTML : null;
  }
  function cartBubble(sections) {
    var b = document.getElementById('cart-icon-bubble'), h = sections && sections['cart-icon-bubble'];
    var inner = h ? cartSectionHTML(h, '.shopify-section') : null;
    if (b && inner != null) b.innerHTML = inner;
  }
  function cartAdded() {
    var d = cartDrawer(), strip = d && $('[data-ef-cart-added]', d);
    if (!strip) return;
    clearTimeout(cartAdded.t);
    strip.hidden = false;
    strip.classList.remove('is-out', 'is-on'); void strip.offsetWidth; strip.classList.add('is-on');
    cartAdded.t = setTimeout(function () {
      if (!strip.isConnected) return;
      strip.classList.add('is-out');
      setTimeout(function () { if (strip.isConnected) strip.hidden = true; }, reduce ? 0 : 450);
    }, 3500);
  }
  function cartPageRender(sections) {
    var wrap = document.getElementById('main-cart-items'), id = wrap && wrap.getAttribute('data-id');
    var target = wrap && $('.js-contents', wrap), inner = id && sections && sections[id] ? cartSectionHTML(sections[id], '.js-contents') : null;
    if (target && inner != null) target.innerHTML = inner;
    cartBubble(sections);
  }
  function cartPublish(data) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', efSource: 'ef-cart', cartData: data });
    }
  }
  function cartError(btn, msg) {
    btn.classList.add('is-error');
    setTimeout(function () { btn.classList.remove('is-error'); }, 2500);
    var box = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (box) box.textContent = msg || (window.cartStrings && window.cartStrings.error) || '';
  }
  function cartAdd(btn) {
    var drawer = btn.closest('cart-drawer'), page = !drawer && document.getElementById('main-cart-items');
    var sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map(function (s) { return s.id; })
      : page ? [page.getAttribute('data-id'), 'cart-icon-bubble'] : ['cart-icon-bubble'];
    var fd = new FormData();
    fd.append('id', btn.getAttribute('data-ef-cart-add'));
    fd.append('quantity', '1');
    fd.append('sections', sections.join(','));
    fd.append('sections_url', window.location.pathname);
    btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
    var wasEmpty = !!(page && document.querySelector('.ef-cart.is-empty'));
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: fd
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) { cartError(btn, res.description || res.message); return; }
        if (drawer) {
          drawer.renderContents(res);
          cartAdded();
          var panel = $('.drawer__inner', drawer);
          if (panel) panel.focus({ preventScroll: true });
        } else if (page) {
          if (wasEmpty) { window.location.reload(); return; } // el resumen y el pago acelerado se pintan con la página
          cartPageRender(res.sections);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        cartPublish(res);
      })
      .catch(function (err) { console.warn('[ef] carrito', err); cartError(btn); })
      .then(function () { if (btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
  }
  function cartPlan(btn) {
    var wrap = document.getElementById('main-cart-items'), id = wrap && wrap.getAttribute('data-id');
    if (!wrap) return;
    btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
    fetch((window.routes && window.routes.cart_change_url) || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        id: btn.getAttribute('data-ef-cart-line'),
        quantity: parseInt(btn.getAttribute('data-ef-cart-qty'), 10) || 1,
        selling_plan: btn.getAttribute('data-ef-cart-plan'),
        sections: [id, 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status || res.errors) { cartError(btn, res.description || res.errors); return; }
        cartPageRender(res.sections);
        cartPublish(res);
      })
      .catch(function (err) { console.warn('[ef] carrito', err); cartError(btn); })
      .then(function () { if (btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
  }
  M.cart = function (root) {
    cartBars(root);
    if (window._efCartBound) return;
    window._efCartBound = true;

    // Re-pintados de Dawn (cantidades, eliminar, añadir): la barra parte del valor anterior
    var pending = false;
    function refresh(scope) {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        cartBars(scope);
        // tras vaciar o re-pintar, si el foco se quedó en el body vuelve al panel (Tab y Esc siguen dentro)
        var d = cartDrawer(), a = document.activeElement;
        if (d && d.classList.contains('active') && (!a || a === document.body)) {
          var p = $('.drawer__inner', d); if (p) p.focus({ preventScroll: true });
        }
      });
    }
    function watch(el) {
      if (!el || !('MutationObserver' in window)) return;
      new MutationObserver(function (list) {
        if (list.some(function (m) { return m.type === 'childList'; })) refresh(el);
      }).observe(el, { childList: true, subtree: true });
    }
    var drawer = cartDrawer();
    watch(drawer);
    watch(document.getElementById('main-cart-footer'));
    if (drawer && 'MutationObserver' in window) {
      var wasOpen = drawer.classList.contains('active'), t = null;
      new MutationObserver(function () {
        var open = drawer.classList.contains('active');
        if (open && !wasOpen) {
          if (!reduce) {
            drawer.classList.add('is-entering');
            clearTimeout(t);
            t = setTimeout(function () { drawer.classList.remove('is-entering'); }, 1000);
          }
          cartBars(drawer, 0);
          // sin transición (reduce) Dawn no llega a mover el foco (espera transitionend): lo llevamos al panel
          setTimeout(function () {
            var p = $('.drawer__inner', drawer);
            if (p && drawer.classList.contains('active') && !drawer.contains(document.activeElement)) p.focus({ preventScroll: true });
          }, 500);
        }
        if (!open && drawer.classList.contains('is-entering')) drawer.classList.remove('is-entering'); // remove() siempre muta: sin el if, bucle
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // "Añadido a tu carrito" tras añadir desde la ficha (product-form publica antes de pintar el cajón)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (e) {
        if (e && e.source === 'product-form') setTimeout(cartAdded, 0);
      });
    }

    document.addEventListener('click', function (e) {
      var add = e.target.closest('[data-ef-cart-add]');
      if (add) { e.preventDefault(); if (!add.classList.contains('is-loading')) cartAdd(add); return; }
      var plan = e.target.closest('[data-ef-cart-plan]');
      if (plan) { e.preventDefault(); if (!plan.classList.contains('is-loading')) cartPlan(plan); return; }
      var close = e.target.closest('[data-ef-cart-close]');
      if (close) { var d = close.closest('cart-drawer'); if (d && d.close) { e.preventDefault(); d.close(); } return; }
      var open = e.target.closest('[data-ef-cart-open]'), dr = cartDrawer();
      if (open && dr && dr.open && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
        e.preventDefault();
        var back = open;
        if (open.closest('#ef-mmenu')) { back = mmenuFrom || document.querySelector('header-drawer summary') || open; mmenuClose(); }
        dr.open(back);
      }
    });
    document.addEventListener('keydown', function (e) {
      var d = cartDrawer();
      if (!d || !d.classList.contains('active')) return;
      if (e.key === 'Escape') { if (!d.contains(document.activeElement)) d.close(); return; } // con el foco dentro: keyup de Dawn
      if (e.key !== 'Tab') return;
      var panel = $('.drawer__inner', d);
      var f = $$('a[href], button, input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])', panel).filter(function (el) {
        return !el.disabled && el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], a = document.activeElement;
      if (!panel.contains(a)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && (a === first || a === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    }, true);
  };

  /* ======================================================================
     33 · RESULTADOS DE BÚSQUEDA — sections/ef-search-page.liquid (+ snippets/ef-sp-card.liquid)
     [data-ef-searchpage]:
     - Pastillas [data-ef-sp-cat="all|c1|c2…"] (aria-pressed): dejan solo las tarjetas [data-ef-sp-item] con ese
       data-cat (los resultados de la página cargada) y vuelven a hacerlas entrar escalonadas (60 ms; sin reduce).
       Actualizan [data-ef-sp-count] (data-one / data-other con [N]; en "all" vuelve el texto original, que puede
       contar más páginas) y [data-ef-sp-empty]. La elegida va a ?cat= (replaceState) y al campo
       [data-ef-sp-catfield] del formulario de orden: al reordenar se conserva.
     - [data-ef-sp-clear] (× del buscador): vacía [data-ef-sp-input] y le da el foco; la × se oculta con el campo vacío.
       Sin JS es un enlace a /search.
     form[data-ef-quickadd] ("Añadir al carrito" de una tarjeta): /cart/add.js por AJAX y abre el cajón igual que
     "Añadir +" del módulo 32 (renderContents de Dawn, franja "Añadido", PUB_SUB cartUpdate, contador). Botón
     .is-loading mientras tanto; error → .is-error + mensaje bajo el botón. Sin cajón o sin JS, el form va a /cart/add.
     ====================================================================== */
  M.searchpage = function (root) {
    $$('[data-ef-searchpage]', root).forEach(function (box) {
      if (box._efSp) return;
      box._efSp = true;
      var pills = $$('[data-ef-sp-cat]', box), items = $$('[data-ef-sp-item]', box);
      var counts = $$('[data-ef-sp-count]', box), empty = $('[data-ef-sp-empty]', box);
      var field = $('[data-ef-sp-catfield]', box);
      counts.forEach(function (c) { c._efTxt = c.textContent; });
      function set(cat, user) {
        if (!pills.some(function (p) { return p.getAttribute('data-ef-sp-cat') === cat; })) cat = 'all';
        var n = 0, shown = [];
        pills.forEach(function (p) {
          var on = p.getAttribute('data-ef-sp-cat') === cat;
          p.classList.toggle('is-on', on);
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on && user && p.scrollIntoView) p.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
        });
        items.forEach(function (it) {
          var ok = cat === 'all' || it.getAttribute('data-cat') === cat;
          it.hidden = !ok;
          if (ok) { n++; shown.push(it); }
        });
        if (user && !reduce) {
          shown.forEach(function (it, i) { it.classList.remove('is-in'); it.style.transitionDelay = Math.min(i, 11) * 60 + 'ms'; });
          void box.offsetWidth;
          shown.forEach(function (it) { it.classList.add('is-in'); });
          setTimeout(function () { shown.forEach(function (it) { it.style.transitionDelay = ''; }); }, 1500);
        } else if (user) {
          shown.forEach(function (it) { it.classList.add('is-in'); });
        }
        counts.forEach(function (c) {
          if (cat === 'all') { c.textContent = c._efTxt; return; }
          var tpl = n === 1 ? c.getAttribute('data-one') : c.getAttribute('data-other');
          if (tpl) c.textContent = tpl.replace('[N]', n);
        });
        if (empty) empty.hidden = n > 0;
        if (field) { field.value = cat; field.disabled = cat === 'all'; }
        if (user && window.history && history.replaceState && window.URL) {
          var u = new URL(window.location.href);
          if (cat === 'all') u.searchParams.delete('cat'); else u.searchParams.set('cat', cat);
          history.replaceState(history.state, '', u.toString());
        }
      }
      pills.forEach(function (p) {
        p.addEventListener('click', function () { set(p.getAttribute('data-ef-sp-cat'), true); });
      });
      if (pills.length && window.URLSearchParams) {
        var c0 = new URLSearchParams(window.location.search).get('cat');
        if (c0) set(c0, false);
      }

      var input = $('[data-ef-sp-input]', box), x = $('[data-ef-sp-clear]', box);
      if (input && x) {
        x.addEventListener('click', function (e) {
          e.preventDefault();
          input.value = '';
          x.hidden = true;
          input.focus();
        });
        input.addEventListener('input', function () { x.hidden = !input.value; });
      }
    });

    if (window._efQuickBound) return;
    window._efQuickBound = true;
    document.addEventListener('submit', function (e) {
      var f = e.target && e.target.closest && e.target.closest('form[data-ef-quickadd]');
      var drawer = cartDrawer();
      if (!f || !drawer || !drawer.renderContents || !drawer.getSectionsToRender || !window.fetch) return; // envío normal
      e.preventDefault();
      var btn = $('[type="submit"]', f) || f;
      if (btn.classList.contains('is-loading')) return;
      var msg = $('.ef-sp__err', f);
      if (msg) msg.remove();
      var fd = new FormData(f);
      fd.append('sections', drawer.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      fd.append('sections_url', window.location.pathname);
      btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: fd
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) {
            cartError(btn, res.description || res.message);
            var p = document.createElement('p');
            p.className = 'ef-sp__err'; p.setAttribute('role', 'alert');
            p.textContent = res.description || res.message || (window.cartStrings && window.cartStrings.error) || '';
            f.appendChild(p);
            return;
          }
          drawer.classList.remove('is-empty');
          if (drawer.setActiveElement) drawer.setActiveElement(btn);
          drawer.renderContents(res);
          cartAdded();
          cartPublish(res);
        })
        .catch(function (err) { console.warn('[ef] añadir desde la búsqueda', err); cartError(btn); })
        .then(function () { if (btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
    });
  };

  /* ======================================================================
     34 · ENCUADRE POR TRAMOS — vídeo recortado (object-fit: cover) cuyo encuadre horizontal cambia según el segundo
     <video data-ef-videopos="0:50, 6.08:20"> · PDP · último slide de la galería ("Tan fácil": en el paso 3 la
     máquina queda a la izquierda). La transición de object-position está en ef-animations.css (41).
     ====================================================================== */
  M.videopos = function (root) {
    $$('video[data-ef-videopos]', root).forEach(function (v) {
      if (v._efVpos) return;
      v._efVpos = true;
      var stops = v.getAttribute('data-ef-videopos').split(',').map(function (p) {
        var a = p.split(':'); return { t: parseFloat(a[0]) || 0, x: parseFloat(a[1]) };
      }).filter(function (p) { return !isNaN(p.x); }).sort(function (a, b) { return a.t - b.t; });
      if (!stops.length) return;
      var cur = null;
      function apply() {
        var x = stops[0].x;
        stops.forEach(function (p) { if (v.currentTime >= p.t) x = p.x; });
        if (x !== cur) { cur = x; v.style.objectPosition = x + '% 50%'; }
      }
      v.addEventListener('timeupdate', apply);
      v.addEventListener('seeked', apply);
      apply();
    });
  };

  /* ======================================================================
     35 · TEXTO DE ARTÍCULO — limpia el HTML importado (Wix) del cuerpo de un artículo
     <div data-ef-prose> {{ article.content }} </div>   · Blog · artículo (sections/ef-article.liquid)
     Quita los párrafos vacíos (&nbsp;) y los <meta> sueltos, y envuelve tablas e iframes para que no desborden.
     Los estilos inline (fuentes, colores) los neutraliza el CSS de la sección.
     ====================================================================== */
  M.prose = function (root) {
    $$('[data-ef-prose]', root).forEach(function (box) {
      if (box._efProse) return;
      box._efProse = true;
      $$('meta', box).forEach(function (m) { m.remove(); });
      $$('p, div', box).forEach(function (p) {
        if (p.querySelector('img, iframe, video, picture, svg, table, input, button')) return;
        if (!p.textContent.replace(/[\s ​]+/g, '')) p.remove();
      });
      // Subtítulos escritos TODO EN MAYÚSCULAS (artículos antiguos) → solo la primera letra en mayúscula
      $$('h1, h2, h3, h4, h5, h6', box).forEach(function (h) {
        var t = h.textContent;
        if (!/[A-Za-zÀ-ÿ]/.test(t) || t !== t.toUpperCase() || t === t.toLowerCase()) return;
        var low = t.toLocaleLowerCase('es');
        var i = low.search(/[a-zà-ÿ]/);
        h.textContent = low.slice(0, i) + low.charAt(i).toLocaleUpperCase('es') + low.slice(i + 1);
      });
      $$('table', box).forEach(function (t) {
        if (t.parentNode.classList && t.parentNode.classList.contains('ef-prose-table')) return;
        var w = document.createElement('div');
        w.className = 'ef-prose-table';
        w.setAttribute('tabindex', '0');
        t.parentNode.insertBefore(w, t);
        w.appendChild(t);
      });
      $$('iframe', box).forEach(function (f) {
        if (f.parentNode.classList && f.parentNode.classList.contains('ef-prose-embed')) return;
        var w = document.createElement('div');
        w.className = 'ef-prose-embed';
        f.parentNode.insertBefore(w, f);
        w.appendChild(f);
      });
    });
  };

  /* ======================================================================
     36 · ÍNDICE DEL ARTÍCULO — se genera desde los títulos del contenido y marca el apartado que se está leyendo
     <div data-ef-toc="h2,h3" data-ef-toc-min="2" data-ef-toc-offset="140">   · raíz (la sección)
       <div data-ef-toc-src> …contenido… </div>                              · de dónde salen los títulos
       <x data-ef-toc-box> … <ol data-ef-toc-list></ol> … </x>                · uno o varios índices (lateral / desplegable)
       <span data-ef-toc-count="[N] apartados"></span>                        · texto con el número de apartados
     Nivel: el primero de la lista con al menos -min títulos (h2; si el artículo no tiene, h3). Sin suficientes
     títulos → los [data-ef-toc-box] quedan ocultos (hidden) y la raíz lleva .is-notoc.
     Enlaces con ancla (#id, ids generados si faltan); el activo lleva .is-on + aria-current. Al tocar uno dentro de
     un <details> (desplegable mobile) se cierra. El salto suave lo da scroll-margin + scroll-behavior (sin movimiento
     con prefers-reduced-motion).
     Modo estático (Centro de ayuda · sections/ef-help): <div data-ef-toc data-ef-toc-static> con los enlaces ya
     escritos en Liquid (<a href="#id" data-ef-toc-link="id">, en tantos sitios como haga falta: tarjetas, lateral,
     pastillas); no se genera nada, solo se marca el activo y se salta. Un carril horizontal [data-ef-toc-rail] se
     desplaza solo para que el enlace activo quede a la vista.
     ====================================================================== */
  M.toc = function (root) {
    $$('[data-ef-toc]', root).forEach(function (box) {
      if (box._efToc) return;
      box._efToc = true;
      var offset = parseInt(box.getAttribute('data-ef-toc-offset'), 10) || 140;
      if (box.hasAttribute('data-ef-toc-static')) {
        var seen = {}, sheads = [];
        $$('[data-ef-toc-link]', box).forEach(function (a) {
          var id = a.getAttribute('data-ef-toc-link'), h = document.getElementById(id);
          if (h && !seen[id]) { seen[id] = true; sheads.push(h); }
        });
        if (!sheads.length) return;
        sheads.forEach(function (h) { h.setAttribute('tabindex', '-1'); });
        return tocWire(box, box, sheads, offset);
      }
      var src = $('[data-ef-toc-src]', box), lists = $$('[data-ef-toc-list]', box), boxes = $$('[data-ef-toc-box]', box);
      if (!src || !lists.length) return;
      var levels = (box.getAttribute('data-ef-toc') || 'h2,h3').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      var min = parseInt(box.getAttribute('data-ef-toc-min'), 10) || 2;
      var heads = [];
      for (var l = 0; l < levels.length; l++) {
        heads = $$(levels[l], src).filter(function (h) { return h.textContent.trim() && !h.closest('[data-ef-toc-skip]'); });
        if (heads.length >= min) break;
      }
      if (heads.length < min) {
        boxes.forEach(function (b) { b.hidden = true; });
        box.classList.add('is-notoc');
        return;
      }
      var used = {};
      function slug(t) {
        var s = t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'apartado';
        var k = s, i = 2;
        while (used[k] || document.getElementById(k)) k = s + '-' + i++;
        used[k] = true;
        return k;
      }
      heads.forEach(function (h) { if (!h.id) h.id = slug(h.textContent.trim()); h.setAttribute('tabindex', '-1'); });
      lists.forEach(function (ol) {
        ol.innerHTML = '';
        heads.forEach(function (h) {
          var li = document.createElement('li'), a = document.createElement('a');
          a.href = '#' + h.id;
          a.textContent = h.textContent.replace(/\s+/g, ' ').trim();
          a.setAttribute('data-ef-toc-link', h.id);
          li.appendChild(a);
          ol.appendChild(li);
        });
      });
      $$('[data-ef-toc-count]', box).forEach(function (c) {
        c.textContent = (c.getAttribute('data-ef-toc-count') || '[N]').replace('[N]', heads.length);
      });
      boxes.forEach(function (b) { b.hidden = false; });
      tocWire(box, src, heads, offset);
    });
  };

  /* Marca el enlace activo según el scroll y hace el salto (lo comparten el índice generado y el estático) */
  function tocWire(box, src, heads, offset) {
    var links = $$('[data-ef-toc-link]', box), rails = $$('[data-ef-toc-rail]', box), cur = null;
    function sync() {
      var i = 0;
      for (var k = 0; k < heads.length; k++) if (heads[k].getBoundingClientRect().top - offset <= 1) i = k;
      var id = heads[i].id;
      if (id === cur) return;
      cur = id;
      links.forEach(function (a) {
        var on = a.getAttribute('data-ef-toc-link') === id;
        a.classList.toggle('is-on', on);
        if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
      });
      // carril horizontal: lleva el activo a la vista sin mover la página en vertical
      rails.forEach(function (r) {
        var a = $('[data-ef-toc-link].is-on', r);
        if (!a || r.scrollWidth <= r.clientWidth) return;
        var left = a.offsetLeft - (r.clientWidth - a.offsetWidth) / 2;
        if (r.scrollTo) r.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
        else r.scrollLeft = left;
      });
    }
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; sync(); });
    }, { passive: true });
    window.addEventListener('resize', sync);
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var h = document.getElementById(a.getAttribute('data-ef-toc-link'));
        if (!h) return;
        e.preventDefault();
        var d = a.closest('details');
        if (d) d.open = false;
        h.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        h.focus({ preventScroll: true });
        if (window.history && history.replaceState) history.replaceState(history.state, '', '#' + h.id);
      });
    });
    sync();
    if (window.location.hash) {
      var t = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (t && src.contains(t)) setTimeout(function () { t.scrollIntoView({ block: 'start' }); }, 60);
    }
  }

  /* ======================================================================
     37 · CARGAR MÁS — pagina un listado sin recargar (con enlace normal a ?page=N como respaldo)
     <div data-ef-more="<id único>">                                · Blog · Últimos artículos
       <x data-ef-more-list> <y data-ef-more-item>… </x>            · donde se añaden las tarjetas de la página siguiente
       <p data-ef-more-seen>Has visto 6 de 50</p>                    · se sustituye por el de la página traída
       <i data-ef-more-bar style="width: 12%"></i>                   · ídem (ancho)
       <a data-ef-more-next href="…?page=2">Cargar más</a>          · se actualiza o desaparece en la última página
     Las tarjetas nuevas entran con el mismo reveal (escalonado 110 ms) y el foco pasa a la primera.
     ====================================================================== */
  M.more = function (root) {
    $$('[data-ef-more]', root).forEach(function (box) {
      var btn = $('[data-ef-more-next]', box), list = $('[data-ef-more-list]', box);
      if (!btn || !list || btn._efMore || !window.fetch || !window.DOMParser) return;
      btn._efMore = true;
      var key = box.getAttribute('data-ef-more');
      btn.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        if (btn.classList.contains('is-loading')) return;
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        fetch(btn.href, { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var nb = null;
            $$('[data-ef-more]', doc).forEach(function (b) { if (b.getAttribute('data-ef-more') === key) nb = b; });
            if (!nb) throw new Error('sin listado');
            var items = $$('[data-ef-more-item]', nb), first = null;
            items.forEach(function (it, i) {
              var n = document.importNode(it, true);
              list.appendChild(n);
              if (!first) first = n;
              if (n.hasAttribute('data-ef-reveal')) {
                if (reduce) n.classList.add('is-in');
                else setTimeout(function () { n.classList.add('is-in'); }, 30 + i * 110);
              }
            });
            var s0 = $('[data-ef-more-seen]', box), s1 = $('[data-ef-more-seen]', nb);
            if (s0 && s1) s0.textContent = s1.textContent;
            var b0 = $('[data-ef-more-bar]', box), b1 = $('[data-ef-more-bar]', nb);
            if (b0 && b1) b0.style.width = b1.style.width;
            var next = $('[data-ef-more-next]', nb);
            if (next) btn.href = next.getAttribute('href');
            else btn.remove();
            var focusable = first && $('a[href]:not([tabindex="-1"])', first);
            if (focusable) focusable.focus({ preventScroll: true });
          })
          .catch(function (err) { console.warn('[ef] cargar más', err); window.location.href = btn.href; })
          .then(function () { if (btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
      });
    });
  };

  /* ======================================================================
     38 · COPIAR ENLACE — copia una URL al portapapeles y lo confirma en el propio botón
     <button data-ef-copy="https://…" data-ef-copy-done="Enlace copiado"><span data-ef-copy-label>Copiar enlace</span></button>
     Sin valor copia la URL actual. El texto vuelve a su estado a los 2 s (.is-done mientras tanto).
     ====================================================================== */
  M.copy = function (root) {
    $$('[data-ef-copy]', root).forEach(function (b) {
      if (b._efCopy) return;
      b._efCopy = true;
      var label = $('[data-ef-copy-label]', b) || b, txt0 = label.textContent, t = null;
      function done() {
        label.textContent = b.getAttribute('data-ef-copy-done') || txt0;
        b.classList.add('is-done');
        clearTimeout(t);
        t = setTimeout(function () { label.textContent = txt0; b.classList.remove('is-done'); }, 2000);
      }
      b.addEventListener('click', function () {
        var url = b.getAttribute('data-ef-copy') || window.location.href;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function () { fallback(url); });
        } else fallback(url);
      });
      function fallback(url) {
        var ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch (e) { window.prompt('', url); }
        ta.remove();
      }
    });
  };

  /* ======================================================================
     39 · BUSCADOR DEL CENTRO DE AYUDA — filtra en el navegador las preguntas y respuestas de la página
     <div data-ef-help data-ef-help-max="5">                                   · raíz (sections/ef-help)
       <form data-ef-help-form> <input data-ef-help-input role="combobox"> …   · action=/search de respaldo sin JS
       <div data-ef-help-panel hidden>
         <div data-ef-help-results> <p data-ef-help-count data-one data-many="[N] respuestas">
           <ul data-ef-help-list role="listbox"> <a data-ef-help-more data-label="Ver todas las preguntas de [T]">
         <div data-ef-help-empty hidden> <p data-ef-help-empty-title data-label="No encontramos nada sobre «[Q]»">
       <x data-ef-help-query="envío">                                          · "Lo más buscado" y sugerencias: lanzan esa búsqueda
       <details data-ef-help-q data-ef-help-topic="Pedidos y envíos" data-ef-help-topic-id="tema-…">
         <x data-ef-help-qtext>pregunta</x> … <x data-ef-help-atext>respuesta</x>
     Sin acentos ni mayúsculas; todas las palabras tienen que aparecer (si una no aparece tal cual, vale su raíz:
     "envío" encuentra "Enviáis"). Primero las que coinciden en la pregunta. Resalta con <mark>. ↑↓ mueven, Enter abre
     la pregunta (la del resultado activo o la primera) y lleva a ella, Esc cierra (y la segunda vez borra). Al abrir
     usa el clic del <summary>, así el acordeón (módulo 11) cierra las demás. Placeholder corto < 990 (data-ph-mobile).
     ====================================================================== */
  M.helpsearch = function (root) {
    $$('[data-ef-help]', root).forEach(function (box) {
      if (box._efHelp) return;
      var form = $('[data-ef-help-form]', box), input = $('[data-ef-help-input]', box), panel = $('[data-ef-help-panel]', box);
      var list = $('[data-ef-help-list]', box), count = $('[data-ef-help-count]', box), more = $('[data-ef-help-more]', box);
      var res = $('[data-ef-help-results]', box), empty = $('[data-ef-help-empty]', box), emptyT = $('[data-ef-help-empty-title]', box);
      if (!form || !input || !panel || !list) return;
      box._efHelp = true;
      var max = parseInt(box.getAttribute('data-ef-help-max'), 10) || 5;
      var uid = (input.id || 'ef-help') + '-opt-';

      function norm(s) { return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
      // normaliza conservando la posición de cada carácter (para resaltar sobre el texto original)
      function map(s) {
        var out = '', idx = [];
        for (var i = 0; i < s.length; i++) {
          var c = norm(s.charAt(i));
          for (var j = 0; j < c.length; j++) { out += c.charAt(j); idx.push(i); }
        }
        return { n: out, idx: idx };
      }
      var STOP = ' el la los las de del al un una unos en mi mis tu se es lo que con por para y o a me te le su sus ';
      function stem(w) { return w.length > 4 ? w.replace(/(es|s|a|e|o)$/, '') : w; }

      var items = $$('[data-ef-help-q]', box).map(function (d) {
        var q = $('[data-ef-help-qtext]', d), a = $('[data-ef-help-atext]', d);
        var qt = q ? q.textContent.replace(/\s+/g, ' ').trim() : '';
        var at = a ? a.textContent.replace(/\s+/g, ' ').trim() : '';
        return { el: d, q: qt, a: at, qm: map(qt), am: map(at), topic: d.getAttribute('data-ef-help-topic') || '',
          tid: d.getAttribute('data-ef-help-topic-id') || '' };
      });

      // rangos [ini, fin) en el texto original donde aparece cada palabra (o su raíz)
      function ranges(m, words) {
        var out = [];
        words.forEach(function (w) {
          var t = m.n.indexOf(w.full) > -1 ? w.full : w.root, p = m.n.indexOf(t);
          while (p > -1 && t) {
            out.push([m.idx[p], m.idx[p + t.length - 1] + 1]);
            p = m.n.indexOf(t, p + t.length);
          }
        });
        return out.sort(function (x, y) { return x[0] - y[0]; });
      }
      function has(m, w) { return m.n.indexOf(w.full) > -1 || m.n.indexOf(w.root) > -1; }
      function paint(el, text, rs) {
        el.textContent = '';
        var pos = 0;
        rs.forEach(function (r) {
          if (r[0] < pos) { if (r[1] > pos) r = [pos, r[1]]; else return; }
          if (r[0] > pos) el.appendChild(document.createTextNode(text.slice(pos, r[0])));
          var mk = document.createElement('mark');
          mk.textContent = text.slice(r[0], r[1]);
          el.appendChild(mk);
          pos = r[1];
        });
        if (pos < text.length) el.appendChild(document.createTextNode(text.slice(pos)));
      }
      // extracto de la respuesta: el principio o, si la coincidencia va lejos, un trozo alrededor
      function excerpt(it, words) {
        var rs = ranges(it.am, words), text = it.a, start = 0, LEN = 120;
        if (rs.length && rs[0][0] > 70) start = text.lastIndexOf(' ', rs[0][0] - 30) + 1;
        var end = Math.min(text.length, start + LEN);
        if (end < text.length) { var sp = text.lastIndexOf(' ', end); if (sp > start) end = sp; }
        var cut = text.slice(start, end), shift = start;
        var rr = rs.map(function (r) { return [r[0] - shift, Math.min(r[1], end) - shift]; }).filter(function (r) { return r[0] >= 0 && r[1] > r[0]; });
        return { text: (start ? '…' : '') + cut + (end < text.length ? '…' : ''), rs: rr.map(function (r) { return start ? [r[0] + 1, r[1] + 1] : r; }) };
      }

      var hits = [], active = -1, lastQ = '';
      function search(raw) {
        var q = norm(raw).replace(/[¿?¡!.,;:()«»"']/g, ' ').trim();
        var words = q.split(/\s+/).filter(function (w) { return w.length > 1 && STOP.indexOf(' ' + w + ' ') < 0; })
          .map(function (w) { return { full: w, root: stem(w) }; });
        hits = [];
        if (!words.length) return close();
        items.forEach(function (it) {
          var all = words.every(function (w) { return has(it.qm, w) || has(it.am, w) || norm(it.topic).indexOf(w.root) > -1; });
          if (!all) return;
          var inQ = words.filter(function (w) { return has(it.qm, w); }).length;
          hits.push({ it: it, score: inQ });
        });
        hits.sort(function (x, y) { return y.score - x.score; });
        render(raw.trim(), words);
      }
      function render(raw, words) {
        list.innerHTML = '';
        active = -1;
        input.removeAttribute('aria-activedescendant');
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        var none = !hits.length;
        if (res) res.hidden = none;
        if (empty) empty.hidden = !none;
        if (none) {
          if (emptyT) emptyT.textContent = (emptyT.getAttribute('data-label') || '«[Q]»').replace('[Q]', raw);
          return;
        }
        if (count) count.textContent = (hits.length === 1 ? count.getAttribute('data-one') : (count.getAttribute('data-many') || '[N]')).replace('[N]', hits.length);
        hits.slice(0, max).forEach(function (h, i) {
          var li = document.createElement('li'), cat = document.createElement('span'), qq = document.createElement('span'), ex = document.createElement('span');
          li.id = uid + i;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.className = 'ef-help-res';
          cat.className = 'ef-help-res__cat'; cat.textContent = h.it.topic;
          qq.className = 'ef-help-res__q'; paint(qq, h.it.q, ranges(h.it.qm, words));
          ex.className = 'ef-help-res__a';
          var e = excerpt(h.it, words); paint(ex, e.text, e.rs);
          li.appendChild(cat); li.appendChild(qq); li.appendChild(ex);
          li.addEventListener('mousedown', function (ev) { ev.preventDefault(); });
          li.addEventListener('click', function () { go(h.it); });
          li.addEventListener('mousemove', function () { if (active !== i) setActive(i); });
          list.appendChild(li);
        });
        if (more) {
          var top = hits[0].it;
          more.textContent = (more.getAttribute('data-label') || '[T]').replace('[T]', top.topic) + ' →';
          more.setAttribute('href', '#' + top.tid);
          more.hidden = !top.tid;
          more._tid = top.tid;
        }
      }
      function setActive(i) {
        var opts = $$('[role="option"]', list);
        if (!opts.length) return;
        active = (i + opts.length) % opts.length;
        opts.forEach(function (o, k) { o.setAttribute('aria-selected', String(k === active)); o.classList.toggle('is-on', k === active); });
        input.setAttribute('aria-activedescendant', opts[active].id);
        var o = opts[active];
        if (o.offsetTop < list.scrollTop) list.scrollTop = o.offsetTop;
        else if (o.offsetTop + o.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = o.offsetTop + o.offsetHeight - list.clientHeight;
      }
      function close() {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
      }
      function go(it) {
        close();
        input.blur();
        var s = $('summary', it.el);
        if (!it.el.open && s) s.click();
        it.el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (s) s.focus({ preventScroll: true });
      }

      input.addEventListener('input', function () { lastQ = input.value; search(input.value); });
      input.addEventListener('focus', function () { if (input.value.trim()) search(input.value); });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (panel.hidden) { if (input.value.trim()) search(input.value); return; }
          e.preventDefault();
          setActive(active < 0 ? (e.key === 'ArrowDown' ? 0 : -1) : active + (e.key === 'ArrowDown' ? 1 : -1));
        } else if (e.key === 'Escape') {
          if (!panel.hidden) { e.preventDefault(); close(); }
          else if (input.value) { e.preventDefault(); input.value = ''; }
        }
      });
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!input.value.trim()) { input.focus(); return; }
        if (panel.hidden || lastQ !== input.value) { lastQ = input.value; search(input.value); }
        if (hits.length) go(hits[Math.max(0, active)].it);
        else input.focus();
      });
      if (more) more.addEventListener('click', function (e) {
        var h = more._tid && document.getElementById(more._tid);
        if (!h) return;
        e.preventDefault();
        close();
        h.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        h.focus({ preventScroll: true });
      });
      $$('[data-ef-help-query]', box).forEach(function (b) {
        b.addEventListener('mousedown', function (e) { if (b.closest('[data-ef-help-panel]')) e.preventDefault(); });
        b.addEventListener('click', function () {
          input.value = b.getAttribute('data-ef-help-query');
          lastQ = input.value;
          input.focus();
          search(input.value);
        });
      });
      // cierra al tocar fuera del buscador
      var wrap = form.parentNode;
      document.addEventListener('pointerdown', function (e) { if (!panel.hidden && !wrap.contains(e.target)) close(); });
      panel.addEventListener('keydown', function (e) { if (e.key === 'Escape') { close(); input.focus(); } });

      // enlace directo a una pregunta (#pregunta-…), al cargar y en hashchange: la abre con el clic del <summary>
      // (el acordeón cierra las demás) y la lleva a pantalla; el hueco del header lo da su scroll-margin-top
      function fromHash(smooth) {
        var id = window.location.hash.slice(1), d = null;
        try { d = id && document.getElementById(decodeURIComponent(id)); } catch (e) { d = null; }
        if (!d || !d.matches('[data-ef-help-q]') || !box.contains(d)) return;
        var s = $('summary', d);
        if (!d.open && s) s.click();
        // tras el salto propio del navegador y el del índice (módulo 36, a los 60 ms)
        setTimeout(function () {
          d.scrollIntoView({ behavior: smooth && !reduce ? 'smooth' : 'auto', block: 'start' });
          if (s) s.focus({ preventScroll: true });
        }, 120);
      }
      fromHash(false);
      window.addEventListener('hashchange', function () { fromHash(true); });

      // placeholder corto en mobile
      var ph = input.getAttribute('placeholder'), phm = input.getAttribute('data-ph-mobile');
      if (phm && window.matchMedia) {
        var mq = window.matchMedia('(max-width: 989px)');
        var setPh = function () { input.setAttribute('placeholder', mq.matches ? phm : ph); };
        setPh();
        if (mq.addEventListener) mq.addEventListener('change', setPh);
      }
    });
  };

  /* ======================================================================
     40 · BURBUJA DE AYUDA (tip) — PDP · "ⓘ Detalles del plan" en la opción de suscripción (sections/ef-main-product)
     [data-ef-tip] > button[data-ef-tip-btn][aria-describedby] + [data-ef-tip-bubble] (role=tooltip, id).
     Abre (.is-open en el contenedor) al pasar el ratón (solo con hover real), al enfocar el botón o al tocarlo;
     tocar/clicar lo deja fijo hasta volver a tocar, tocar fuera o Esc. La flechita apunta al botón (--ef-tip-x,
     relativo al .ef-pdp__plan / padre posicionado de la burbuja). El botón va dentro del <label> del plan: el clic
     no cambia el plan (preventDefault). La aparición es CSS 45. Con el plan desactivado (radio disabled del <label>,
     p. ej. producto agotado) no se abre.
     ====================================================================== */
  M.tip = function (root) {
    $$('[data-ef-tip]', root).forEach(function (tip) {
      if (tip._efTip) return;
      tip._efTip = true;
      var btn = $('[data-ef-tip-btn]', tip), bubble = $('[data-ef-tip-bubble]', tip);
      if (!btn || !bubble) return;
      var pinned = false, hoverMq = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
      function place() {
        var host = bubble.offsetParent || tip.parentNode;
        if (!host || !host.getBoundingClientRect) return;
        var hr = host.getBoundingClientRect(), br = btn.getBoundingClientRect();
        bubble.style.setProperty('--ef-tip-x', Math.max(16, Math.round(br.left - hr.left + 12)) + 'px');
      }
      function off() {
        var lab = tip.closest('label'), r = lab && lab.querySelector('input');
        return !!(r && r.disabled);
      }
      function open() {
        if (tip.classList.contains('is-open') || off()) return;
        place();
        tip.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
      function close() {
        pinned = false;
        tip.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (pinned) { close(); return; }
        pinned = true; open();
      });
      bubble.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
      tip.addEventListener('mouseenter', function () { if (!hoverMq || hoverMq.matches) open(); });
      tip.addEventListener('mouseleave', function () { if ((!hoverMq || hoverMq.matches) && !pinned) close(); });
      btn.addEventListener('focus', function () { if (btn.matches(':focus-visible')) open(); });
      btn.addEventListener('blur', function () { if (!pinned) close(); });
      document.addEventListener('pointerdown', function (e) { if (tip.classList.contains('is-open') && !tip.contains(e.target)) close(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && tip.classList.contains('is-open')) { close(); if (tip.contains(document.activeElement)) btn.focus(); }
      });
      window.addEventListener('resize', function () { if (tip.classList.contains('is-open')) place(); });
    });
  };

  /* ======================================================================
     41 · CIFRA DE LA VARIANTE (vcount) — PDP · "Qué hay dentro" (sections/ef-pdp-inside), "N cápsulas en tu pack"
     <span data-ef-count="30" data-ef-suffix="" data-ef-vcount>: el número lo pinta el Liquid con la variante actual;
     al cambiar de variante en la caja de compra (evento ef:variant del módulo 18 · buybox) se pone el número de la
     opción 0 ("60 Cápsulas" → 60). Si el contador (módulo 8) aún no ha corrido, contará hasta el número nuevo.
     ====================================================================== */
  function efVcount(v) {
    var n = v && v.options ? parseInt(String(v.options[0]).replace(/[^0-9]/g, ''), 10) : NaN;
    if (!(n > 0)) return;
    $$('[data-ef-vcount]').forEach(function (el) {
      if (el.getAttribute('data-ef-count') === String(n)) return;
      el.setAttribute('data-ef-count', n);
      el.textContent = (el.getAttribute('data-ef-prefix') || '') + n + (el.getAttribute('data-ef-suffix') || '');
    });
  }
  M.vcount = function () {
    // la variante con la que arrancó la caja (p. ej. "30 Cápsulas" por defecto sin ?variant=; el Liquid de esta
    // sección no la conoce) y, después, cada cambio
    $$('[data-ef-buybox]').forEach(function (box) { if (box._efVariant) efVcount(box._efVariant); });
    if (window._efVcount) return;
    window._efVcount = true;
    document.addEventListener('ef:variant', function (e) { efVcount(e.detail && e.detail.variant); });
  };

  /* ======================================================================
     42 · AGOTADO EN LA CAJA DE COMPRA (soldout) — PDP · sections/ef-main-product.liquid
     <button data-ef-buybox-submit> (CTA principal y el de la barra fija, que vive fuera de [data-ef-buybox]):
     con la variante elegida agotada → disabled + aria-disabled="true"; disponible → se quitan. El texto ("Agotado")
     ya lo pone el módulo 18. Escucha ef:variant del módulo 18 y arranca con la variante inicial (box._efVariant).
     ====================================================================== */
  function efSoldout(box, v) {
    if (!box || !v) return;
    var scope = box.closest('[data-ef-section]') || document;
    $$('[data-ef-buybox-submit]', scope).forEach(function (b) {
      b.disabled = !v.available;
      if (v.available) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
    });
  }
  M.soldout = function () {
    $$('[data-ef-buybox]').forEach(function (box) { if (box._efVariant) efSoldout(box, box._efVariant); });
    if (window._efSoldout) return;
    window._efSoldout = true;
    document.addEventListener('ef:variant', function (e) {
      var box = e.target && e.target.closest && e.target.closest('[data-ef-buybox]');
      efSoldout(box, e.detail && e.detail.variant);
    });
  };

  /* ====================================================================== */

  function init(root) {
    root = root || document;
    if (root !== document) {
      if (root.dataset && root.dataset[INIT] === '1') return;
      if (root.dataset) root.dataset[INIT] = '1';
    }
    Object.keys(M).forEach(function (k) {
      try { M[k](root); } catch (err) { console.warn('[ef] módulo ' + k, err); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  // Editor de temas: al recargar una sección, se vuelve a enganchar solo esa
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    if (el && el.dataset) delete el.dataset[INIT];
    init(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target;
    if (el && el.dataset) delete el.dataset[INIT];
  });

  window.EF = { init: init, modules: M, reduce: reduce };
})();
